import React, { useState, useMemo, useEffect, useCallback } from 'react';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { gapi } from 'gapi-script';
import { Calendar, Clock, User, Phone, Mail, FileText, Plus, X, ChevronLeft, ChevronRight, Edit2, Trash2, Search, Image, MessageCircle, Upload, BarChart2, AlertCircle, Link2, ShoppingBag, BookOpen, Package, CheckCircle, ClipboardList, ShoppingCart, Rss, Star, MessageSquare, MailOpen, Users, Palette } from 'lucide-react';
import './mobile-overrides.css';

// Importa le librerie Firebase necessarie
import { initializeApp } from 'firebase/app';
import { getAuth, signInWithEmailAndPassword, createUserWithEmailAndPassword, onAuthStateChanged, signOut } from 'firebase/auth';
import { getFirestore, collection, addDoc, getDocs, doc, updateDoc, deleteDoc, onSnapshot, query, setDoc, getDoc, where, writeBatch, serverTimestamp } from 'firebase/firestore';

const firebaseConfig = {
  apiKey: "AIzaSyAWrM7K_xZLk2DQZYC_6UNTwyZ9A24qHe4",
  authDomain: "atelier-inkhub.firebaseapp.com",
  projectId: "atelier-inkhub",
  storageBucket: "atelier-inkhub.firebasestorage.app",
  messagingSenderId: "256146756986",
  appId: "1:256146756986:web:04cecb733e631dbc39f2c3"
};

const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);

// Configurazione OAuth Gmail
const GOOGLE_CLIENT_ID = '2050424523-vlcgi0v9ilm4v9574bjrpsc5jevgih19.apps.googleusercontent.com';
const GOOGLE_API_SCOPES = 'https://www.googleapis.com/auth/gmail.send https://www.googleapis.com/auth/userinfo.email openid';
const DEMO_LOGIN_EMAIL = 'inkhub@tattoo.com';
const DEMO_LOGIN_PASSWORD = 'demo123';

// Confirmation Modal Component
const ConfirmationModal = ({ message, onConfirm, onCancel, show }) => {
  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-xl p-6 shadow-2xl max-w-sm w-full border border-gray-700">
        <div className="flex items-center gap-3 mb-4">
          <AlertCircle className="w-6 h-6 text-yellow-400" />
          <h3 className="text-xl font-semibold text-white">Conferma Azione</h3>
        </div>
        <p className="text-gray-300 mb-6">{message}</p>
        <div className="flex justify-end gap-3">
          <button
            onClick={onCancel}
            className="px-4 py-2 bg-gray-700 text-gray-200 rounded-lg hover:bg-gray-600 transition-colors"
          >
            Annulla
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Conferma
          </button>
        </div>
      </div>
    </div>
  );
};

// Message Box Component
const MessageBox = ({ message, type, onClose, show }) => {
  if (!show) return null;

  let bgColor = 'bg-blue-600';
  let icon = <MessageCircle className="w-5 h-5" />;

  if (type === 'error') {
    bgColor = 'bg-red-600';
    icon = <AlertCircle className="w-5 h-5" />;
  } else if (type === 'success') {
    bgColor = 'bg-green-600';
    icon = <CheckCircle className="w-5 h-5" />;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className={`${bgColor} rounded-xl p-6 shadow-2xl max-w-sm w-full flex flex-col items-center text-white`}>
        <div className="flex items-center gap-3 mb-4">
          {icon}
          <h3 className="text-xl font-semibold">Messaggio</h3>
        </div>
        <p className="text-center mb-6">{message}</p>
        <button
          onClick={onClose}
          className="px-4 py-2 bg-white bg-opacity-20 text-white rounded-lg hover:bg-opacity-30 transition-colors"
        >
          Chiudi
        </button>
      </div>
    </div>
  );
};


// (moved avatar clamp CSS injection hook inside component below)

const TattooScheduler = () => {
  // Inject global CSS for avatar/logo clamp (inside component to respect hooks rules)
  useEffect(() => {
    const style = document.createElement('style');
    style.setAttribute('data-inkhub', 'avatar-clamp');
    style.innerHTML = `
      img.rounded-full { object-fit: cover; }
      .inkhub-avatar, .topbar-logo img {
        width: 48px; height: 48px; max-width: 48px; max-height: 48px;
        object-fit: cover; border-radius: 9999px;
      }

      /* ===== Global mobile layout (phones only, safe & scoped) ===== */
      @media (max-width: 640px) {
        /* Header on phones: horizontal row -> [app logo | title + subtitle | studio logo] ; actions go to next row */
        body[data-inkhub-mobile="1"] header {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          flex-wrap: wrap; /* allow actions to drop on next line */
        }
        /* Brand: title + subtitle stacked (subtitle on next line) */
        body[data-inkhub-mobile="1"] header h1 {
          display: block;
          margin: 0;
        }
        body[data-inkhub-mobile="1"] header h1 + p {
          display: block;
          margin: 0.15rem 0 0 0; /* small spacing under title */
        }
        body[data-inkhub-mobile="1"] header p.text-sm {
          font-size: 0.75rem !important;  /* ~12px */
          line-height: 1rem !important;   /* ~16px */
          white-space: normal;            /* evita il troncamento */
        }
        /* Push the round studio logo to the far right on the first row */
        body[data-inkhub-mobile="1"] header img.rounded-full {
          width: 64px; height: 64px; object-fit: cover;
          margin-left: auto;
          order: 2;
        }
        /* Place the actions toolbar (usually a flex .items-center) on the next line */
        body[data-inkhub-mobile="1"] header .flex.items-center {
          order: 3;
          flex-basis: 100%;
          gap: 0.5rem;
          justify-content: flex-start;
        }

        /* Clamp any header image (logo/avatar) to 48px on phones */
        body[data-inkhub-mobile="1"] header img { width: 48px; height: 48px; object-fit: cover; }

        /* Make any primary toolbar wrap safely */
        /* (already handled above for header .flex.items-center) */

        /* Cards: allow horizontal scroll if needed and fix truncate */
        body[data-inkhub-mobile="1"] .bg-gray-800.rounded-xl { overflow-x: auto; }
        body[data-inkhub-mobile="1"] .bg-gray-800.rounded-xl,
        body[data-inkhub-mobile="1"] .bg-gray-800.rounded-xl * { min-width: 0; }

        /* Headline: slightly reduce to fit */
        body[data-inkhub-mobile="1"] h1 { font-size: 1.75rem; line-height: 2.1rem; }
        body[data-inkhub-mobile="1"] h2 { font-size: 1.25rem; line-height: 1.75rem; }

        /* Promo box compatto solo su smartphone */
        body[data-inkhub-mobile="1"] [data-promo-hero="1"] {
          padding: 0.5rem !important;
          margin: 0.25rem 0 !important;
          border-radius: 0.5rem !important;
        }

        /* Extra spazio SOTTO al promo box solo su smartphone */
        body[data-inkhub-mobile="1"] [data-promo-hero="1"] {
          margin-top: 0.25rem !important;   /* resta minimo sopra */
          margin-bottom: 1.5rem !important;   /* più spazio sotto (≈16px) */
        }

        body[data-inkhub-mobile="1"] [data-promo-hero="1"] h2,
        body[data-inkhub-mobile="1"] [data-promo-hero="1"] h3 {
          font-size: 0.9rem !important;
          line-height: 1.1rem !important;
        }

        body[data-inkhub-mobile="1"] [data-promo-hero="1"] p {
          font-size: 0.7rem !important;
          line-height: 0.9rem !important;
        }

        body[data-inkhub-mobile="1"] [data-promo-hero="1"] button {
          font-size: 0.75rem !important;
          padding: 0.3rem 0.6rem !important;
        }
          
      }
    `;
    document.head.appendChild(style);
    return () => { try { document.head.removeChild(style); } catch (_) {} };
  }, []);

  // Inietta data-promo-hero="1" sul box promo con pulsante "Visita lo Shop"
  useEffect(() => {
    const btn = document.querySelector('button, a');
    if (!btn) return;
    if (/Visita lo Shop/i.test(btn.textContent || '')) {
      const card = btn.closest('div');
      if (card) card.setAttribute('data-promo-hero', '1');
    }
  }, []);

  // Track phone viewport and expose as data-attribute for safe CSS scoping
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(max-width: 640px)');
    const apply = () => {
      try { document.body.setAttribute('data-inkhub-mobile', mq.matches ? '1' : '0'); } catch (_) {}
    };
    apply();
    mq.addEventListener ? mq.addEventListener('change', apply) : mq.addListener(apply);
    return () => {
      mq.removeEventListener ? mq.removeEventListener('change', apply) : mq.removeListener(apply);
    };
  }, []);
  // Stato per token Gmail
  const [gmailToken, setGmailToken] = useState(null);
  const [gmailEmail, setGmailEmail] = useState('');
  // -- Persistenza token Gmail nella sessione del browser --
  const GMAIL_TOKEN_KEY = 'inkhub_gmail_token';
  const GMAIL_TOKEN_EXP_KEY = 'inkhub_gmail_token_exp';
  const GMAIL_EMAIL_KEY = 'inkhub_gmail_email';
  const [user, setUser] = useState(null);
const [loadingAuth, setLoadingAuth] = useState(true);

  // Carica gapi client e GIS
useEffect(() => {
  try {
    (async () => { await ensureGsiLoaded(); })();
    if (gapi && typeof gapi.load === 'function') {
      function start() { try { gapi.client.init({}); } catch (e) { console.warn('gapi.client.init failed', e); } }
      gapi.load('client', start);
    } else {
      console.warn('gapi non disponibile al mount');
    }
  } catch (e) {
    console.warn('Errore inizializzazione gapi:', e);
  }
}, []);



// Carica lo script Google Identity Services se non presente
const ensureGsiLoaded = () => new Promise((resolve) => {
  if (window.google?.accounts?.oauth2) return resolve();
  const id = 'google-gis';
  const existing = document.getElementById(id);
  if (existing) { existing.addEventListener('load', () => resolve()); return; }
  const s = document.createElement('script');
  s.id = id;
  s.src = 'https://accounts.google.com/gsi/client';
  s.async = true;
  s.defer = true;
  s.onload = () => resolve();
  document.head.appendChild(s);
});

// Garantisce un token Gmail valido: prova da sessione, altrimenti silent refresh (no popup)
const ensureValidGmailToken = useCallback(async () => {
  await ensureGsiLoaded();

  // 1) Prova token salvato in sessione
  const savedToken = sessionStorage.getItem(GMAIL_TOKEN_KEY);
  const savedExp = parseInt(sessionStorage.getItem(GMAIL_TOKEN_EXP_KEY) || '0', 10);
  const savedEmail = sessionStorage.getItem(GMAIL_EMAIL_KEY) || '';
  if (savedToken && savedExp && Date.now() < savedExp) {
    setGmailToken(savedToken);
    if (gapi?.client?.setToken) gapi.client.setToken({ access_token: savedToken });
    if (savedEmail) setGmailEmail(savedEmail);
    return savedToken;
  }

  // 2) Silent refresh via GIS (richiede cookie Google attivi)
  if (!(window.google?.accounts?.oauth2)) return null;

  return new Promise((resolve) => {
    try {
      const tokenClient = window.google.accounts.oauth2.initTokenClient({
        client_id: GOOGLE_CLIENT_ID,
        scope: GOOGLE_API_SCOPES,
        callback: async (resp) => {
          if (resp?.access_token) {
            const token = resp.access_token;
            const expiresInSec = typeof resp.expires_in === 'number' ? resp.expires_in : 3600;
            const expAt = Date.now() + (expiresInSec - 60) * 1000; // rinnovo 1 min prima

            setGmailToken(token);
            if (gapi?.client?.setToken) gapi.client.setToken({ access_token: token });
            sessionStorage.setItem(GMAIL_TOKEN_KEY, token);
            sessionStorage.setItem(GMAIL_TOKEN_EXP_KEY, String(expAt));

            // Mantieni anche l'email se già nota
            const email = gmailEmail || sessionStorage.getItem(GMAIL_EMAIL_KEY) || '';
            if (email) setGmailEmail(email);

            resolve(token);
          } else {
            resolve(null);
          }
        },
      });
      tokenClient.requestAccessToken({ prompt: '' }); // nessun popup
    } catch (e) {
      console.warn('Silent token refresh fallito:', e);
      resolve(null);
    }
  });
}, [gmailEmail]);
  // State variables for managing UI views and data
  const [view, setView] = useState('calendar');

  // Keep current section on <body> for scoped CSS targeting (no runtime DOM queries needed)
  useEffect(() => {
    try {
      document.body.setAttribute('data-inkhub-section', String(view || ''));
    } catch (_) {}
    return () => {
      // no-op; we intentionally keep last known section
    };
  }, [view]);

  // Evita salti/spostamenti quando si cambia sezione (calendar ↔ appointments, ecc.)
useEffect(() => {
  try {
    window.scrollTo({ top: 0, behavior: 'auto' });
  } catch (_) {
    // fallback per browser vecchi
    window.scrollTo(0, 0);
  }
}, [view]);
  // Invia un page_view a GA4 quando cambia la vista
useEffect(() => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'page_view', {
      page_path: '/' + view,
      page_title: 'InkHub - ' + view,
    });
  }
}, [view]);
document.title = 'InkHub - ' + view;
// NEW: invia un page_view iniziale quando gtag è pronto
useEffect(() => {
  if (typeof window === 'undefined') return;
  let tries = 0;
  const t = setInterval(() => {
    if (window.gtag) {
      window.gtag('event', 'page_view', { page_path: '/', page_title: 'InkHub - app' });
      document.title = 'InkHub - app';
      clearInterval(t);
    } else if (++tries > 10) {
      clearInterval(t); // smette dopo ~5s se gtag non è disponibile
    }
  }, 500);
  return () => clearInterval(t);
}, []);
  const [calendarView, setCalendarView] = useState('month');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [appointments, setAppointments] = useState([]); // Inizializzato vuoto per caricare da Firebase
  const [clients, setClients] = useState([]); // Inizializzato vuoto per caricare da Firebase
  const [artists, setArtists] = useState([]); // Ora gestiti su Firebase
const [userStudioName, setUserStudioName] = useState('InkHub');
const [userStudioPhone, setUserStudioPhone] = useState('');
const [userStudioEmail, setUserStudioEmail] = useState('');
const [studioLogo, setStudioLogo] = useState('');
const [showStudioSettings, setShowStudioSettings] = useState(false);
const [editingStudioName, setEditingStudioName] = useState('');
const [editingStudioPhone, setEditingStudioPhone] = useState('');
const [showReminderAlert, setShowReminderAlert] = useState(false);
// --- Stato modale invio reminder ---
const [showReminderModal, setShowReminderModal] = useState(false);
const [reminderCandidates, setReminderCandidates] = useState([]); // appuntamenti nelle prossime 48h
const [selectedReminderIds, setSelectedReminderIds] = useState(new Set());
const [previewApt, setPreviewApt] = useState(null);
const [pendingReminders, setPendingReminders] = useState([]);
const [reminderTimeout, setReminderTimeout] = useState(null);
const [dismissedReminders, setDismissedReminders] = useState(new Set());
const [showImageModal, setShowImageModal] = useState(false);
const [selectedImage, setSelectedImage] = useState(null);
// Stato per abilitare/disabilitare le notifiche
const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  // Component for uploading the studio logo
  const StudioLogoUploader = ({ logo, onChangeLogo }) => {
    const handleFileChange = (e) => {
      const file = e.target.files[0];
      if (!file) return;
      if (file.size > 600 * 1024) {
        openMessageBox('Immagine troppo grande, scegli un logo sotto i 600kb!', 'error');
        return;
      }
      const img = new window.Image();
      img.onload = () => {
        if (img.width > 1024 || img.height > 1024) {
          openMessageBox('Logo troppo grande, max 1024x1024px!', 'error');
          return;
        }
        const reader = new FileReader();
        reader.onload = (ev) => onChangeLogo(ev.target.result);
        reader.readAsDataURL(file);
      };
      img.src = URL.createObjectURL(file);
    };

    return (
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-300 mb-1">Logo Studio</label>
        {logo && (
          <div className="flex flex-col items-center gap-2">
            <img
              src={logo}
              alt="Logo Studio"
              className="rounded-full w-16 h-16 object-cover border border-gray-400 mb-1"
            />
            <button
              type="button"
              onClick={() => onChangeLogo('')}
              className="text-red-400 text-xs underline hover:text-red-600"
            >
              Rimuovi logo
            </button>
          </div>
        )}
        <input
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          className="mt-2 text-sm"
        />
        <div className="text-xs text-gray-400 mt-1">Max 600kb, max 1024x1024px. Preview tonda.</div>
      </div>
    );
  };
  // Assicurati che artists sia sempre un array
React.useEffect(() => {
  if (!Array.isArray(artists)) {
    setArtists([]);
  }
}, [artists]);
  const [showArtistForm, setShowArtistForm] = useState(false);
  const [editingArtist, setEditingArtist] = useState(null);
  // Form data for new/editing artists
  const [artistFormData, setArtistFormData] = useState({
  name: '',
  specialties: '',
  email: '',
  phone: '',
  notes: ''
  });
  const [showAppointmentForm, setShowAppointmentForm] = useState(false);
  const [showClientForm, setShowClientForm] = useState(false);
  const [editingAppointment, setEditingAppointment] = useState(null);
  const [editingClient, setEditingClient] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [appointmentMonth, setAppointmentMonth] = useState(new Date());
  const [selectedArtist, setSelectedArtist] = useState('all');

  // Stato per l'autenticazione
  const [loginEmail, setLoginEmail] = useState(DEMO_LOGIN_EMAIL);
  const [loginPassword, setLoginPassword] = useState(DEMO_LOGIN_PASSWORD);


  // State for custom confirmation modal
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmMessage, setConfirmMessage] = useState('');

  // State for custom message box
  const [showMessageBox, setShowMessageBox] = useState(false);
  const [messageBoxMessage, setMessageBoxMessage] = useState('');
  const [messageBoxType, setMessageBoxType] = useState('info');

  // --- MessageBox helpers (definiti PRIMA di usarli negli effetti) ---
  const openMessageBox = useCallback((message, type = 'info') => {
    setMessageBoxMessage(message);
    setMessageBoxType(type);
    setShowMessageBox(true);
  }, []);

  const closeMessageBox = () => {
    setShowMessageBox(false);
    setMessageBoxMessage('');
    setMessageBoxType('info');
  };

  // --- Google Analytics helper ---
const gaEvent = useCallback((name, params = {}) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', name, params);
  }
}, []);

  // --- Dati demo (definiti PRIMA di checkDemoData/useEffect) ---
  const createDemoData = useCallback(async (userId) => {
    try {
      // Crea artisti demo
      const demoArtists = [
        { name: 'Marco Rossi', specialties: 'Realistico, Blackwork', email: 'marco@studio.com', phone: '333-1234567', notes: 'Specializzato in ritratti' },
        { name: 'Elena Bianchi', specialties: 'Giapponese, Colorato', email: 'elena@studio.com', phone: '333-2345678', notes: 'Ama i tatuaggi tradizionali giapponesi' },
        { name: 'Luca Verde', specialties: 'Geometrico, Minimal', email: 'luca@studio.com', phone: '333-3456789', notes: 'Perfetto per design moderni e minimal' }
      ];
      for (const artist of demoArtists) {
        await addDoc(collection(db, `users/${userId}/artists`), artist);
      }

      // Crea clienti demo
      const demoClients = [
        { name: 'Giulia Ferrari', phone: '339-1111111', email: 'giulia.ferrari@email.com', notes: 'Prima volta, molto emozionata',
          allergies: 'Nessuna', preferredZones: 'Braccio', totalSessions: 1, consent: true, consentDate: '2024-12-15', preferredArtist: 'Marco Rossi' },
        { name: 'Roberto Neri', phone: '339-2222222', email: 'roberto.neri@email.com', notes: 'Cliente abituale',
          allergies: 'Lattice', preferredZones: 'Schiena, Petto', totalSessions: 3, consent: true, consentDate: '2024-11-20', preferredArtist: 'Elena Bianchi' },
        { name: 'Sofia Romano', phone: '339-3333333', email: 'sofia.romano@email.com', notes: 'Ama i design geometrici',
          allergies: 'Nessuna', preferredZones: 'Polso, Caviglia', totalSessions: 2, consent: true, consentDate: '2024-12-01', preferredArtist: 'Luca Verde' }
      ];
      for (const client of demoClients) {
        await addDoc(collection(db, `users/${userId}/clients`), client);
      }

      // Crea appuntamenti demo
      const today = new Date();
      const demoAppointments = [
        { clientName: 'Giulia Ferrari', clientPhone: '339-1111111', clientEmail: 'giulia.ferrari@email.com',
          date: new Date(today.getTime() + 2 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          time: '10:00', duration: 3, project: 'Rosa realistica sul braccio', notes: 'Prima seduta, design già approvato',
          deposit: 100, totalPrice: 350, photos: [], sketches: [], status: 'confirmed', artist: 'Marco Rossi' },
        { clientName: 'Roberto Neri', clientPhone: '339-2222222', clientEmail: 'roberto.neri@email.com',
          date: new Date(today.getTime() + 5 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          time: '14:30', duration: 4, project: 'Drago giapponese - continuazione', notes: 'Terza seduta per completare la schiena',
          deposit: 150, totalPrice: 500, photos: [], sketches: [], status: 'confirmed', artist: 'Elena Bianchi' },
        { clientName: 'Sofia Romano', clientPhone: '339-3333333', clientEmail: 'sofia.romano@email.com',
          date: new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          time: '16:00', duration: 2, project: 'Mandala geometrico polso', notes: 'Design minimalista, cliente molto precisa',
          deposit: 80, totalPrice: 250, photos: [], sketches: [], status: 'confirmed', artist: 'Luca Verde' }
      ];
      for (const appointment of demoAppointments) {
        await addDoc(collection(db, `users/${userId}/appointments`), appointment);
      }

      openMessageBox('Dati demo creati con successo! Puoi iniziare a esplorare l\'app.', 'success');
    } catch (error) {
      console.error('Errore nella creazione dei dati demo:', error);
      openMessageBox('Errore nella creazione dei dati demo.', 'error');
    }
  }, [openMessageBox]);

  // Form data for new/editing appointments
  const [formData, setFormData] = useState({
    clientName: '',
    clientPhone: '',
    clientEmail: '',
    date: '',
    time: '',
    duration: 2,
    project: '',
    notes: '',
    deposit: '',
    totalPrice: '',
    photos: [],
    sketches: [],
    status: 'confirmed',
    artist: ''
  });

  // Form data for new/editing clients
  const [clientFormData, setClientFormData] = useState({
    name: '',
    phone: '',
    email: '',
    notes: '',
    allergies: '',
    preferredZones: '',
    referencePhotos: [],
    consent: false,
    preferredArtist: ''
  });

  // Funzioni per gestire l'autenticazione
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setLoadingAuth(false);
    });
    return () => unsubscribe();
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (loginEmail.trim() !== DEMO_LOGIN_EMAIL || loginPassword !== DEMO_LOGIN_PASSWORD) {
      openMessageBox(`Usa le credenziali demo: ${DEMO_LOGIN_EMAIL} / ${DEMO_LOGIN_PASSWORD}`, 'error');
      return;
    }
    try {
      let userCredential;
      try {
        userCredential = await signInWithEmailAndPassword(auth, DEMO_LOGIN_EMAIL, DEMO_LOGIN_PASSWORD);
      } catch (error) {
        if (error?.code === 'auth/user-not-found' || error?.code === 'auth/invalid-credential') {
          userCredential = await createUserWithEmailAndPassword(auth, DEMO_LOGIN_EMAIL, DEMO_LOGIN_PASSWORD);
          await setDoc(doc(db, 'users', userCredential.user.uid), {
            email: DEMO_LOGIN_EMAIL,
            studioName: 'InkHub',
            createdAt: new Date(),
            studioLogo: '',
            notificationsEnabled: true,
          }, { merge: true });
        } else {
          throw error;
        }
      }
      
      // Carica il nome studio dal database
      const userDoc = await getDoc(doc(db, 'users', userCredential.user.uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        console.log('User data from DB:', userData); // DEBUG
        setUserStudioName(userData.studioName || 'InkHub');
      } else {
        console.log('User document does not exist'); // DEBUG
      }
      
      openMessageBox('Login effettuato con successo!', 'success');
      gaEvent('login');
    } catch (error) {
      let msg = 'Impossibile effettuare il login. Riprova.';
      switch (error?.code) {
        case 'auth/user-not-found':
          msg = 'Account demo non disponibile. È necessario inizializzarlo nuovamente.';
          break;
        case 'auth/invalid-credential':
        case 'auth/wrong-password':
          msg = 'L\'account demo non è allineato. Riprova: se il problema persiste va rigenerato su Firebase.';
          break;
        case 'auth/email-already-in-use':
          msg = 'L\'account demo esiste già ma non accetta queste credenziali. Va ripristinato sul progetto Firebase.';
          break;
        case 'auth/too-many-requests':
          msg = 'Troppi tentativi non riusciti. Attendi qualche minuto e riprova.';
          break;
        case 'auth/network-request-failed':
          msg = 'Problema di connessione. Controlla la rete Internet e riprova.';
          break;
        case 'auth/invalid-email':
          msg = 'L\'indirizzo email inserito non è valido. Controlla e riprova.';
          break;
        default:
          // Mantieni un fallback generico senza dettagli tecnici
          // console.error(error); // opzionale per debug
          msg = 'Accesso non riuscito. Verifica che l’account demo sia attivo e riprova.';
          break;
      }
      openMessageBox(msg, 'error');
    }
  };

const handleLogout = async () => {
    await signOut(auth);
    sessionStorage.removeItem(GMAIL_TOKEN_KEY);
    sessionStorage.removeItem(GMAIL_TOKEN_EXP_KEY);
    sessionStorage.removeItem(GMAIL_EMAIL_KEY);
    openMessageBox('Logout effettuato!', 'info');
    gaEvent('logout');
  };
const handleUpdateStudioSettings = async () => {
  try {
    if (!editingStudioName.trim()) {
      openMessageBox('Il nome dello studio è obbligatorio', 'error');
      return;
    }

    // Aggiorna nel database (includendo stato notifiche)
    await updateDoc(doc(db, 'users', user.uid), {
      studioName: editingStudioName.trim(),
      studioPhone: editingStudioPhone.trim(),
      studioLogo: studioLogo,
      notificationsEnabled: notificationsEnabled, // Salva stato notifiche
    });

    // Aggiorna lo stato locale
    setUserStudioName(editingStudioName.trim());
    setUserStudioPhone(editingStudioPhone.trim());

    // Chiudi il modal
    setShowStudioSettings(false);
    
    openMessageBox('Impostazioni aggiornate con successo!', 'success');
  } catch (error) {
    console.error('Errore aggiornamento impostazioni:', error);
    openMessageBox('Errore nell\'aggiornamento delle impostazioni', 'error');
  }
}; // <-- CHIUSURA CORRETTA

const checkPendingReminders = useCallback(async () => {
  if (!user) return;
  try {
    const now = new Date();
    const twoDaysFromNow = new Date(now.getTime() + (2 * 24 * 60 * 60 * 1000));
    
    const appointmentsRef = collection(db, `users/${user.uid}/appointments`);
    const querySnapshot = await getDocs(appointmentsRef);
    
    const upcomingWithoutReminder = [];

    // Controlla se è stato impostato un timeout per ricordare più tardi
    const reminderSnooze = localStorage.getItem('reminderSnooze');
    if (reminderSnooze) {
      const snoozeTime = new Date(reminderSnooze);
      if (new Date() < snoozeTime) {
        console.log('⏰ Reminder snoozed fino a:', snoozeTime);
        return; // Esce dalla funzione se il tempo di snooze non è ancora scaduto
      }
    }
    
    querySnapshot.forEach((docSnap) => {
      const apt = { id: docSnap.id, ...docSnap.data() };
      const appointmentDate = new Date(apt.date);
      
      // Controlla se l'appuntamento è entro 2 giorni E non ha ricevuto reminder E non è stato dismissato
      if (
        appointmentDate >= now &&
        appointmentDate <= twoDaysFromNow &&
        !apt.reminderSent &&
        !dismissedReminders.has(apt.id)
      ) {
        upcomingWithoutReminder.push(apt);
      }
    });
    
    if (upcomingWithoutReminder.length > 0) {
  setPendingReminders(upcomingWithoutReminder);
  setShowReminderAlert(true);
} else {
  // Nessun reminder in sospeso: azzera lo stato per evitare residui di altri account
  setPendingReminders([]);
  setShowReminderAlert(false);
}
  } catch (error) {
    console.error('Errore controllo reminder:', error);
  }
}, [user, dismissedReminders]);
  // Handler per "Ricorda tra 2h" (snooze reminders)
  const snoozeReminders = () => {
    const snoozeUntil = new Date();
    snoozeUntil.setHours(snoozeUntil.getHours() + 2);
    localStorage.setItem('reminderSnooze', snoozeUntil.toISOString());
    setShowReminderAlert(false);
  };
// Carica nome studio quando l'utente è loggato
useEffect(() => {
  const loadStudioName = async () => {
    if (user) {
      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          console.log('Loading studio data:', userData);
          setUserStudioName(userData.studioName || 'InkHub');
          setUserStudioPhone(userData.studioPhone || '');
          setUserStudioEmail(userData.email || '');
          setStudioLogo(userData.studioLogo || '');
          setNotificationsEnabled(userData.notificationsEnabled ?? true);
          setDismissedMonthlyAlerts(new Set(userData.dismissedMonthlyAlerts || []));
          setGmailEmail(userData.gmailEmail || '');
        } else {
          console.log('Creating user document');
          await setDoc(doc(db, 'users', user.uid), {
            email: user.email,
            studioName: 'InkHub',
            createdAt: new Date(),
            studioLogo: ''
          });
          setUserStudioName('InkHub');
          setStudioLogo('');
        }
      } catch (error) {
        console.error('Error loading studio name:', error);
        setUserStudioName('InkHub');
        setStudioLogo('');
      }
    }
  };
  
  loadStudioName();
  checkPendingReminders();
}, [user, checkPendingReminders]);

// Aggiorna ultimo accesso e attività utente su Firestore
useEffect(() => {
  if (user) {
    const updateLastActive = async () => {
      try {
        const userRef = doc(db, 'users', user.uid);
        const userDocSnap = await getDoc(userRef);
        const today = new Date().toISOString().split('T')[0]; // es: "2025-08-06"

        if (userDocSnap.exists()) {
          const data = userDocSnap.data();

          // Aggiorna solo se è il primo accesso del giorno
          if (data.lastActive !== today) {
            await updateDoc(userRef, {
              lastActive: today,
              lastLogin: new Date(),
            });
          }
        } else {
          // Se il documento utente non esiste ancora, crealo
          await setDoc(userRef, {
            email: user.email,
            lastActive: today,
            lastLogin: new Date(),
            createdAt: new Date(),
          });
        }
      } catch (err) {
        console.error("Errore aggiornamento lastActive:", err);
      }
    };

    updateLastActive();
  }
}, [user]);

// Controllo periodico Reminder
useEffect(() => {
  if (!user) return;
  
  // Controllo ogni 30 minuti se ci sono reminder da inviare
  const interval = setInterval(() => {
    checkPendingReminders();
  }, 30 * 60 * 1000); // 30 minuti
  
  return () => {
    clearInterval(interval);
    // Pulisci anche il timeout del reminder
    if (reminderTimeout) {
      clearTimeout(reminderTimeout);
    }
  };
}, [user, reminderTimeout, checkPendingReminders]);

useEffect(() => {
  // Controllo pulizia foto scadute ogni volta che l'utente fa login
  if (user) {
    cleanExpiredPhotos();
    
    // Controllo giornaliero (ogni 24 ore)
    const dailyCleanup = setInterval(() => {
      cleanExpiredPhotos();
    }, 24 * 60 * 60 * 1000); // 24 ore
    
    return () => clearInterval(dailyCleanup);
  }
}, [user]);

useEffect(() => {
  // Carica dismissed reminders dal localStorage
  const saved = localStorage.getItem('dismissedReminders');
  if (saved) {
    setDismissedReminders(new Set(JSON.parse(saved)));
  }
}, []);

// 🔄 Reset cross-account state quando cambia utente (nuovo login o logout)
const currentUserId = user?.uid || null;
useEffect(() => {
  // Svuota banner e promemoria pendenti per non mostrare dati del profilo precedente
  setMonthlyAlerts([]);
  setPendingReminders([]);
  setShowReminderAlert(false);
  setDismissedMonthlyAlerts(new Set());
  // Evita che lo snooze dei reminder di un altro account rimanga attivo
  localStorage.removeItem('reminderSnooze');
}, [currentUserId]);

// Popola il form quando iniziamo a modificare un appuntamento
useEffect(() => {
  if (editingAppointment) {
    setFormData({
      clientName: editingAppointment.clientName || '',
      clientPhone: editingAppointment.clientPhone || '',
      clientEmail: editingAppointment.clientEmail || '',
      date: editingAppointment.date || '',
      time: editingAppointment.time || '',
      duration: editingAppointment.duration || 2,
      project: editingAppointment.project || '',
      notes: editingAppointment.notes || '',
      deposit: editingAppointment.deposit || '',
      totalPrice: editingAppointment.totalPrice || '',
      photos: editingAppointment.photos || [],
      sketches: editingAppointment.sketches || [],
      status: editingAppointment.status || 'confirmed',
      artist: editingAppointment.artist || ''
    });
  }
}, [editingAppointment]);

// Popola il form quando iniziamo a modificare un cliente
useEffect(() => {
  if (editingClient) {
    setClientFormData({
      name: editingClient.name || '',
      phone: editingClient.phone || '',
      email: editingClient.email || '',
      notes: editingClient.notes || '',
      allergies: editingClient.allergies || '',
      preferredZones: editingClient.preferredZones || '',
      referencePhotos: editingClient.referencePhotos || [],
      consent: editingClient.consent || false,
      preferredArtist: editingClient.preferredArtist || ''
    });
  }
}, [editingClient]);


  // Funzioni per gestire i dati con Firestore
  useEffect(() => {
    if (user) {
// Listener per gli artisti
 const qArtists = query(collection(db, `users/${user.uid}/artists`));
 const unsubscribeArtists = onSnapshot(qArtists, (snapshot) => {
   const fetchedArtists = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
   setArtists(fetchedArtists);
 }, (error) => {
   console.error("Errore nel caricamento degli artisti:", error);
   openMessageBox("Errore nel caricamento degli artisti.", "error");
 });

      // Listener per gli appuntamenti
      const qAppointments = query(collection(db, `users/${user.uid}/appointments`));
      const unsubscribeAppointments = onSnapshot(qAppointments, (snapshot) => {
        const fetchedAppointments = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setAppointments(fetchedAppointments);
      }, (error) => {
        console.error("Errore nel caricamento degli appuntamenti:", error);
        openMessageBox("Errore nel caricamento degli appuntamenti.", "error");
      });

      // Listener per i clienti
      const qClients = query(collection(db, `users/${user.uid}/clients`));
      const unsubscribeClients = onSnapshot(qClients, (snapshot) => {
        const fetchedClients = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setClients(fetchedClients);
      }, (error) => {
        console.error("Errore nel caricamento dei clienti:", error);
        openMessageBox("Errore nel caricamento dei clienti.", "error");
      });

      // Controlla se creare dati demo (primo accesso)
      const checkDemoData = async () => {
        try {
          console.log("🔍 Controllo dati demo...");
          const appointmentsSnapshot = await getDocs(query(collection(db, `users/${user.uid}/appointments`)));
          const clientsSnapshot = await getDocs(query(collection(db, `users/${user.uid}/clients`)));
          const artistsSnapshot = await getDocs(query(collection(db, `users/${user.uid}/artists`)));
          // Carica nome studio e logo
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            setUserStudioName(userData.studioName || 'InkHub');
            setStudioLogo(userData.studioLogo || '');
          }
          
          console.log("📊 Dati trovati:");
          console.log("- Appuntamenti:", appointmentsSnapshot.size);
          console.log("- Clienti:", clientsSnapshot.size);
          console.log("- Artisti:", artistsSnapshot.size);
          
          // Se tutte le collezioni sono vuote, è il primo accesso
          if (appointmentsSnapshot.empty && clientsSnapshot.empty && artistsSnapshot.empty) {
            console.log("🎉 Primo accesso rilevato! Mostro popup dati demo...");
            setTimeout(() => {
              openConfirmModal(
                'Benvenuto! Vuoi che creiamo alcuni dati demo (artisti, clienti e appuntamenti) per iniziare a esplorare l\'app?',
                () => createDemoData(user.uid)
              );
            }, 2000);
          } else {
            console.log("📝 Dati esistenti trovati, non mostro popup demo");
          }
        } catch (error) {
          console.error('❌ Errore nel controllo dei dati demo:', error);
        }
      };

     checkDemoData();

      return () => {
	unsubscribeArtists();
        unsubscribeAppointments();
        unsubscribeClients();
      };
    } else {
      setAppointments([]);
      setClients([]);
    }
  }, [user, createDemoData, openMessageBox]);

  // Functions to manage confirmation modal
  const openConfirmModal = (message, action) => {
    setConfirmMessage(message);
    setConfirmAction(() => action);
    setShowConfirmModal(true);
  };

  const closeConfirmModal = () => {
    setShowConfirmModal(false);
    setConfirmMessage('');
    setConfirmAction(null);
  };

  const handleConfirm = () => {
    if (confirmAction) {
      confirmAction();
    }
    closeConfirmModal();
  };


  // Calculate statistics based on appointments and clients
  const stats = useMemo(() => {
    const currentMonth = new Date().getMonth();
    const currentYear = new Date().getFullYear();

    let filteredAppointmentsForStats = appointments;
    if (selectedArtist !== 'all') {
      filteredAppointmentsForStats = appointments.filter(apt => apt.artist === selectedArtist);
    }

    const monthlyAppointments = filteredAppointmentsForStats.filter(apt => {
      const aptDate = new Date(apt.date);
      return aptDate.getMonth() === currentMonth && aptDate.getFullYear() === currentYear;
    });

    const totalRevenue = monthlyAppointments.reduce((sum, apt) => sum + apt.totalPrice, 0);
    const totalDeposits = monthlyAppointments.reduce((sum, apt) => sum + apt.deposit, 0);

    const artistStats = {};
    if (Array.isArray(artists)) {
  artists.forEach(artist => {
    const artistName = typeof artist === 'object' ? artist.name : artist;
    const artistAppts = appointments.filter(apt => {
      const aptDate = new Date(apt.date);
      return aptDate.getMonth() === currentMonth &&
             aptDate.getFullYear() === currentYear &&
             apt.artist === artistName;
    });
    artistStats[artistName] = {
      appointments: artistAppts.length,
      revenue: artistAppts.reduce((sum, apt) => sum + apt.totalPrice, 0)
    };
  });
}

    let filteredClientsForStats = clients;
    if (selectedArtist !== 'all') {
      filteredClientsForStats = clients.filter(c => c.preferredArtist === selectedArtist);
    }

    return {
      monthlyAppointments: monthlyAppointments.length,
      totalRevenue,
      totalDeposits,
      artistStats,
      totalClients: filteredClientsForStats.length,
      newClients: filteredClientsForStats.filter(c => {
        const consentDate = new Date(c.consentDate);
        return consentDate.getMonth() === currentMonth && consentDate.getFullYear() === currentYear;
      }).length
    };
  }, [appointments, clients, artists, selectedArtist]);

  // Helper function to get all days in a given month for calendar display
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];

    for (let i = startingDayOfWeek - 1; i >= 0; i--) {
      const prevDate = new Date(year, month, -i);
      days.push({ date: prevDate, currentMonth: false });
    }

    for (let i = 1; i <= daysInMonth; i++) {
      days.push({ date: new Date(year, month, i), currentMonth: true });
    }

    const remainingDays = 42 - days.length;
    for (let i = 1; i <= remainingDays; i++) {
      days.push({ date: new Date(year, month + 1, i), currentMonth: false });
    }

    return days;
  };

  // Helper function to get days of the week for week view
  const getWeekDays = (date) => {
    const week = [];
    const startOfWeek = new Date(date);
    const day = startOfWeek.getDay();
    const diff = startOfWeek.getDate() - day;
    startOfWeek.setDate(diff);

    for (let i = 0; i < 7; i++) {
      const day = new Date(startOfWeek);
      day.setDate(startOfWeek.getDate() + i);
      week.push(day);
    }

    return week;
  };

  // Helper function to format date to YYYY-MM-DD string based on local time
  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Get appointments for a specific date, filtered by artist
  const getAppointmentsForDate = (date) => {
  const dateStr = formatDate(date);
  let filtered = appointments.filter(apt => apt.date === dateStr);

  if (selectedArtist !== 'all') {
    filtered = filtered.filter(apt => apt.artist === selectedArtist);
  }

  return filtered.map(apt => ({
    ...apt,
    colorClass: artistColors[apt.artist] || 'bg-gray-600'
  }));
};

  // Handle navigation to previous period (month, week, or day)
  const handlePrevPeriod = () => {
    const newDate = new Date(selectedDate);
    if (calendarView === 'month') {
      newDate.setMonth(newDate.getMonth() - 1);
    } else if (calendarView === 'week') {
      newDate.setDate(newDate.getDate() - 7);
    } else if (calendarView === 'day') {
      newDate.setDate(newDate.getDate() - 1);
    }
    setSelectedDate(newDate);
  };

  // Handle navigation to next period (month, week, or day)
  const handleNextPeriod = () => {
    const newDate = new Date(selectedDate);
    if (calendarView === 'month') {
      newDate.setMonth(newDate.getMonth() + 1);
    } else if (calendarView === 'week') {
      newDate.setDate(newDate.getDate() + 7);
    } else if (calendarView === 'day') {
      newDate.setDate(newDate.getDate() + 1);
    }
    setSelectedDate(newDate);
  };

  // Handle click on a date in the calendar to switch to day view and show appointments for that day
  const handleDateClick = (date) => {
    setSelectedDate(date);
    setCalendarView('day');
  };

  // Handle submission of the appointment form
  const handleSubmitAppointment = async () => {
    if (!user) {
      openMessageBox('Devi essere loggato per aggiungere appuntamenti.', 'error');
      return;
    }
    if (!formData.clientName || !formData.clientPhone || !formData.date ||
        !formData.time || !formData.project || !formData.artist) {
      openMessageBox('Per favorer compila tutti i campi obbligatori', 'error');
      return;
    }

    try {
      if (editingAppointment) {
        const aptRef = doc(db, `users/${user.uid}/appointments`, editingAppointment.id);
        await updateDoc(aptRef, { ...formData, deposit: parseFloat(formData.deposit) || 0, totalPrice: parseFloat(formData.totalPrice) || 0 });
        openMessageBox('Appuntamento aggiornato con successo!', 'success');
        gaEvent('appointment_updated', {
  artist: formData.artist || 'n/a',
  value: parseFloat(formData.totalPrice) || 0,
});
      } else {
        // 1) Determina o crea il cliente e ricava il clientId
        let clientIdToUse = null;
        const existingClient = clients.find(c => c.name === formData.clientName);

        if (existingClient) {
          clientIdToUse = existingClient.id;
          // incrementa le sessioni
          const clientRef = doc(db, `users/${user.uid}/clients`, existingClient.id);
          await updateDoc(clientRef, { totalSessions: (existingClient.totalSessions || 0) + 1 });
        } else {
          const clientDocRef = await addDoc(collection(db, `users/${user.uid}/clients`), {
            name: formData.clientName,
            phone: formData.clientPhone,
            email: formData.clientEmail,
            totalSessions: 1,
            notes: '',
            allergies: '',
            preferredZones: '',
            consent: false,
            consentDate: '',
            preferredArtist: formData.artist || ''
          });
          clientIdToUse = clientDocRef.id;
        }

        // 2) Crea l'appuntamento includendo sempre clientId
        const newAppointment = {
          ...formData,
          clientId: clientIdToUse,
          deposit: parseFloat(formData.deposit) || 0,
          totalPrice: parseFloat(formData.totalPrice) || 0,
          reminderSent: false
        };
        await addDoc(collection(db, `users/${user.uid}/appointments`), newAppointment);

        openMessageBox('Appuntamento aggiunto con successo!', 'success');
        gaEvent('appointment_created', {
  artist: formData.artist || 'n/a',
  value: parseFloat(formData.totalPrice) || 0,
});
      }
      resetForm();
    } catch (error) {
      console.error("Errore nella gestione dell'appuntamento:", error);
      openMessageBox(`Errore: ${error.message}`, 'error');
    }
  };

  // Propaga le modifiche del cliente a tutti gli appuntamenti collegati
  const propagateClientUpdate = async (client) => {
    if (!user) return;
    try {
      const apptsCol = collection(db, `users/${user.uid}/appointments`);

      // Raccogli tutti i documenti appuntamento da aggiornare
      const refs = [];

      // 1) Appuntamenti che già usano clientId
      try {
        const qById = query(apptsCol, where('clientId', '==', client.id));
        const s1 = await getDocs(qById);
        s1.forEach(d => refs.push(d.ref));
      } catch (e) { /* ignore */ }

      // 2) Backfill: appuntamenti storici che usano solo il nome
      try {
        const qByName = query(apptsCol, where('clientName', '==', client.name));
        const s2 = await getDocs(qByName);
        s2.forEach(d => {
          if (!refs.find(r => r.id === d.ref.id)) refs.push(d.ref);
        });
      } catch (e) { /* ignore */ }

      if (refs.length === 0) return;

      const batch = writeBatch(db);
      refs.forEach(ref => {
        batch.update(ref, {
          clientId: client.id,
          clientName: client.name,
          clientEmail: client.email,
          clientPhone: client.phone ?? null,
          updatedAt: serverTimestamp(),
        });
      });
      await batch.commit();
      console.log(`✅ propagateClientUpdate: aggiornati ${refs.length} appuntamenti`);
    } catch (err) {
      console.error('❌ Errore in propagateClientUpdate:', err);
    }
  };

  // Handle submission of the client form

  // Handle submission of the client form
  const handleSubmitClient = async () => {
    if (!user) {
      openMessageBox('Devi essere loggato per aggiungere clienti.', 'error');
      return;
    }
    if (!clientFormData.name || !clientFormData.phone || !clientFormData.email) {
      openMessageBox('Nome, telefono ed email sono obbligatori', 'error');
      return;
    }

    try {
      if (editingClient) {
        const clientRef = doc(db, `users/${user.uid}/clients`, editingClient.id);
        await updateDoc(clientRef, { ...clientFormData });
        // Propaga subito le modifiche a tutti gli appuntamenti collegati
        await propagateClientUpdate({
          id: editingClient.id,
          name: clientFormData.name,
          email: clientFormData.email,
          phone: clientFormData.phone,
        });
        openMessageBox('Cliente aggiornato con successo!', 'success');
        gaEvent('client_updated');
      } else {
        await addDoc(collection(db, `users/${user.uid}/clients`), {
          ...clientFormData,
          totalSessions: 0,
          consentDate: clientFormData.consent ? new Date().toISOString().split('T')[0] : '',
          preferredArtist: clientFormData.preferredArtist || ''
        });
        openMessageBox('Cliente aggiunto con successo!', 'success');
        gaEvent('client_created');
      }
      resetClientForm();
    } catch (error) {
      console.error("Errore nella gestione del cliente:", error);
      openMessageBox(`Errore: ${error.message}`, 'error');
    }
  };
// Handle submission of the artist form
const handleSubmitArtist = async () => {
  if (!user) {
    openMessageBox('Devi essere loggato per aggiungere artisti.', 'error');
    return;
  }
  if (!artistFormData.name) {
    openMessageBox('Il nome dell\'artista è obbligatorio', 'error');
    return;
  }

  try {
    if (editingArtist) {
      const artistRef = doc(db, `users/${user.uid}/artists`, editingArtist.id);
      await updateDoc(artistRef, { ...artistFormData });
      openMessageBox('Artista aggiornato con successo!', 'success');
      gaEvent('artist_updated');
    } else {
      await addDoc(collection(db, `users/${user.uid}/artists`), artistFormData);
      openMessageBox('Artista aggiunto con successo!', 'success');
      gaEvent('artist_created');
      }
      resetArtistForm();
      // Ricarica gli artisti dopo aver salvato
      setTimeout(() => loadArtists(), 500);
    } catch (error) {
    console.error("Errore nella gestione dell'artista:", error);
    openMessageBox(`Errore: ${error.message}`, 'error');
  }
};

  // Reset appointment form state
  const resetForm = () => {
    setFormData({
      clientName: '',
      clientPhone: '',
      clientEmail: '',
      date: '',
      time: '',
      duration: 2,
      project: '',
      notes: '',
      deposit: '',
      totalPrice: '',
      photos: [],
      sketches: [],
      status: 'confirmed',
      artist: ''
    });
    setShowAppointmentForm(false);
    setEditingAppointment(null);
  };

  // Cancella appuntamento selezionato
const handleDeleteAppointment = async () => {
  if (!user) {
    openMessageBox('Devi essere loggato per eliminare appuntamenti.', 'error');
    return;
  }
  if (!editingAppointment?.id) {
    openMessageBox('Nessun appuntamento da eliminare.', 'error');
    return;
  }

  openConfirmModal(
    "Sei sicuro di voler cancellare questo appuntamento? L’azione è irreversibile.",
    async () => {
      try {
        await deleteDoc(doc(db, `users/${user.uid}/appointments`, editingAppointment.id));
        setShowAppointmentForm(false);
        setEditingAppointment(null);
        openMessageBox('Appuntamento cancellato.', 'success');
        gaEvent('appointment_deleted');
      } catch (err) {
        console.error(err);
        openMessageBox('Errore durante la cancellazione. Riprova.', 'error');
      }
    }
  );
};

  // Reset client form state
  const resetClientForm = () => {
    setClientFormData({
      name: '',
      phone: '',
      email: '',
      notes: '',
      allergies: '',
      preferredZones: '',
      referencePhotos: [],
      consent: false,
      preferredArtist: ''
    });
    setShowClientForm(false);
    setEditingClient(null);
  };

// Reset artist form state
const resetArtistForm = () => {
  setArtistFormData({
    name: '',
    specialties: '',
    email: '',
    phone: '',
    notes: ''
  });
  setShowArtistForm(false);
  setEditingArtist(null);
};

// Handle deletion of an artist using custom confirmation modal
const handleDeleteArtist = async (artistId) => {
  if (!user) {
    openMessageBox('Devi essere loggato per eliminare artisti.', 'error');
    return;
  }

  try {
    // Trova l'artista da eliminare
    const artistToDelete = artists.find(a => a.id === artistId);
    if (!artistToDelete) {
      openMessageBox('Artista non trovato.', 'error');
      return;
    }

    // Trova gli appuntamenti associati
    const artistAppointments = appointments.filter(apt => apt.artist === artistToDelete.name);

    // Prepara messaggio di conferma
    let confirmMessage = `Sei sicuro di voler eliminare l'artista "${artistToDelete.name}"?`;
    if (artistAppointments.length > 0) {
      confirmMessage += ` Verranno eliminati anche ${artistAppointments.length} appuntamento${artistAppointments.length > 1 ? 'i' : ''} associato${artistAppointments.length > 1 ? 'i' : ''}.`;
    }

    // Mostra conferma
    openConfirmModal(confirmMessage, async () => {
  try {
    // Elimina artista
    await deleteDoc(doc(db, `users/${user.uid}/artists`, artistId));

    // Elimina appuntamenti collegati
    for (const appointment of artistAppointments) {
      await deleteDoc(doc(db, `users/${user.uid}/appointments`, appointment.id));
    }

    openMessageBox(
      `Artista eliminato con successo${artistAppointments.length > 0 ? ` insieme a ${artistAppointments.length} appuntamento/i` : ''}!`,
      'success'
    );

    // 👇 Tracciamento GA: quanti appuntamenti sono stati rimossi
    gaEvent('artist_deleted', { appointments_removed: artistAppointments.length });
  } catch (error) {
    console.error("Errore durante l'eliminazione:", error);
    openMessageBox(`Errore: ${error.message}`, 'error');
  }
});
  } catch (error) {
    console.error("Errore nell'eliminazione dell'artista:", error);
    openMessageBox(`Errore: ${error.message}`, 'error');
  }
};


// Funzione per caricare artisti manualmente
const loadArtists = async () => {
  if (!user) return;
  
  try {
    const artistsSnapshot = await getDocs(query(collection(db, `users/${user.uid}/artists`)));
    const fetchedArtists = artistsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    console.log("✅ Artisti caricati manualmente:", fetchedArtists);
    setArtists(fetchedArtists);
  } catch (error) {
    console.error("❌ Errore caricamento artisti:", error);
  }
};


// UTF-8 safe Base64URL encoder
const toBase64Url = (str) => {
  const utf8 = new TextEncoder().encode(str);
  let bin = '';
  for (let i = 0; i < utf8.length; i++) bin += String.fromCharCode(utf8[i]);
  const b64 = btoa(bin);
  return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/,'');
};

// Costruisce l'HTML dell'email di promemoria (identico a quello inviato)
const buildReminderEmailHTML = (apt) => {
  return `
<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; background: #ffffff;">
  <div style="background: linear-gradient(135deg, #6366f1, #8b5cf6); padding: 20px; text-align: center; color: white;">
    <h2 style="margin: 0;">🎨 ${userStudioName}</h2>
    <p style="margin: 5px 0 0 0;">Promemoria Appuntamento</p>
  </div>
  <div style="padding: 25px;">
    <p>Ciao <strong>${apt.clientName}</strong>,</p>
    <p>Ti ricordiamo il tuo appuntamento per il tatuaggio:</p>
    <table style="width: 100%; background: #f8f9fa; padding: 15px; border-radius: 8px; border-collapse: collapse;">
      <tr>
        <td style="padding: 5px 10px; width: 20%;"><strong>📅 Data:</strong></td>
        <td style="padding: 5px 10px; width: 30%;">${new Date(apt.date).toLocaleDateString('it-IT')}</td>
        <td style="padding: 5px 10px; width: 20%;"><strong>⏰ Ora:</strong></td>
        <td style="padding: 5px 10px; width: 30%;">${apt.time}</td>
      </tr>
      <tr>
        <td style="padding: 5px 10px;"><strong>👨‍🎨 Artista:</strong></td>
        <td style="padding: 5px 10px;">${apt.artist}</td>
        <td style="padding: 5px 10px;"><strong>⏱️ Durata:</strong></td>
        <td style="padding: 5px 10px;">${apt.duration} ore</td>
      </tr>
    </table>
    <div style="margin: 15px 0;">
      <p style="margin: 5px 0;"><strong>🎨 Progetto:</strong> ${apt.project}</p>
      ${apt.notes && apt.notes !== 'Nessuna nota particolare' ? `<p style="margin: 5px 0;"><strong>📝 Note:</strong> ${apt.notes}</p>` : ''}
    </div>
    <div style="background: #fff3cd; padding: 15px; border-radius: 8px; margin: 20px 0; text-align: center;">
      <p style="margin: 0 0 10px 0; font-weight: bold;">📞 Contatti ${userStudioName}</p>
      <p style="margin: 0;">Tel: ${userStudioPhone} | Email: ${userStudioEmail}</p>
    </div>
    <p style="text-align: center; font-size: 18px; color: #6366f1; margin: 20px 0;">Ti aspettiamo! 🎨</p>
    <p style="text-align: center; font-size: 12px; color: #666; margin: 20px 0 0 0;">
      ⚠️ Email automatica - NON rispondere<br>
      <em>In collaborazione con L'Atelier Tattoo Supply</em>
    </p>
  </div>
</div>`;
};

// Appuntamenti confermati nelle prossime 48h (con email)
const getUpcomingAppointmentsWithin48h = () => {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const tomorrowStr = tomorrow.toISOString().split('T')[0];

  const dayAfterTomorrow = new Date();
  dayAfterTomorrow.setDate(dayAfterTomorrow.getDate() + 2);
  const dayAfterTomorrowStr = dayAfterTomorrow.toISOString().split('T')[0];

  return appointments.filter(apt =>
    (apt.date === tomorrowStr || apt.date === dayAfterTomorrowStr) &&
    apt.clientEmail &&
    apt.status === 'confirmed' &&
    !apt.reminderSent
  );
};

// Apre il modal con la lista di clienti + anteprima
const openReminderModal = () => {
  const list = getUpcomingAppointmentsWithin48h();
  if (!list.length) {
    openMessageBox('Nessun appuntamento confermato nelle prossime 48h con email valida.', 'info');
    return;
  }
  setReminderCandidates(list);
  setSelectedReminderIds(new Set(list.map(a => a.id)));
  setPreviewApt(list[0]);
  setShowReminderModal(true);
};

// Toggle/Select all
const toggleReminderSelection = (id) => {
  setSelectedReminderIds(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
};
const selectAllReminders = () => setSelectedReminderIds(new Set(reminderCandidates.map(a => a.id)));
const deselectAllReminders = () => setSelectedReminderIds(new Set());

// Invia i reminder solo per gli ID selezionati
const sendSelectedReminders = async () => {
  if (!selectedReminderIds.size) {
    openMessageBox('Seleziona almeno un cliente.', 'error');
    return;
  }

  // Assicura token valido
  let tokenToUse = gmailToken;
  if (!tokenToUse) tokenToUse = await ensureValidGmailToken();
  if (!tokenToUse) {
    openMessageBox('Devi collegare Gmail dalle Impostazioni per inviare i reminder.', 'error');
    return;
  }

  try {
    let sentCount = 0;
    for (const apt of reminderCandidates) {
      if (!selectedReminderIds.has(apt.id)) continue;

      const emailHTML = buildReminderEmailHTML(apt);

      const rawEmail =
        `From: ${gmailEmail || userStudioEmail || user.email}\r\n` +
        `Reply-To: ${userStudioEmail || gmailEmail || user.email}\r\n` +
        `To: ${apt.clientEmail}\r\n` +
        `Subject: =?UTF-8?B?${toBase64Url('Promemoria: Appuntamento tatuaggio ' + new Date(apt.date).toLocaleDateString('it-IT'))}?=\r\n` +
        `Content-Type: text/html; charset=UTF-8\r\n` +
        `MIME-Version: 1.0\r\n\r\n` +
        emailHTML;

      const encodedMessage = toBase64Url(rawEmail);

      await gapi.client.request({
        path: 'https://gmail.googleapis.com/gmail/v1/users/me/messages/send',
        method: 'POST',
        body: { raw: encodedMessage }
      });

      sentCount++;
      await updateDoc(doc(db, `users/${user.uid}/appointments`, apt.id), { reminderSent: true, reminderSentAt: new Date() });
      await new Promise(r => setTimeout(r, 800)); // throttling soft
    }

    setShowReminderModal(false);
    if (sentCount) {
      openMessageBox(`${sentCount} reminder inviati con successo!`, 'success');
    } else {
      openMessageBox('Nessun reminder inviato.', 'info');
    }
  } catch (e) {
    console.error('Errore invio reminder:', e);
    openMessageBox('Errore nell\'invio dei reminder.', 'error');
  }
};

// Apre il modale di selezione/anteprima reminder
const sendBulkReminders = () => {
  openReminderModal();
};

// Handle deletion of a client using custom confirmation modal
const handleDeleteClient = async (clientId) => {
  if (!user) {
    openMessageBox('Devi essere loggato per eliminare clienti.', 'error');
    return;
  }

  try {
    // Prima conta gli appuntamenti del cliente
    const clientToDelete = clients.find(c => c.id === clientId);
    if (!clientToDelete) {
      openMessageBox('Cliente non trovato.', 'error');
      return;
    }

    // Conta appuntamenti di questo cliente
    const clientAppointments = appointments.filter(apt => 
      apt.clientName === clientToDelete.name
    );

    let confirmMessage;
    if (clientAppointments.length === 0) {
      confirmMessage = `Sei sicuro di voler eliminare il cliente "${clientToDelete.name}"?`;
    } else {
      confirmMessage = `Sei sicuro di voler eliminare il cliente "${clientToDelete.name}"? Verranno eliminati anche ${clientAppointments.length} appuntamento${clientAppointments.length > 1 ? 'i' : ''} associato${clientAppointments.length > 1 ? 'i' : ''}.`;
    }

    openConfirmModal(confirmMessage, async () => {
  try {
    // Elimina il cliente
    await deleteDoc(doc(db, `users/${user.uid}/clients`, clientId));

    // Elimina tutti gli appuntamenti del cliente
    for (const appointment of clientAppointments) {
      await deleteDoc(doc(db, `users/${user.uid}/appointments`, appointment.id));
    }

    if (clientAppointments.length === 0) {
      openMessageBox('Cliente eliminato con successo!', 'success');
    } else {
      openMessageBox(`Cliente eliminato con successo insieme a ${clientAppointments.length} appuntamento${clientAppointments.length > 1 ? 'i' : ''}!`, 'success');
    }

    // 👇 Tracciamento GA: quanti appuntamenti sono stati rimossi
    gaEvent('client_deleted', { appointments_removed: clientAppointments.length });
  } catch (error) {
    console.error("Errore nell'eliminazione del cliente:", error);
    openMessageBox(`Errore: ${error.message}`, 'error');
  }
});
  } catch (error) {
    console.error("Errore nel controllo degli appuntamenti:", error);
    openMessageBox(`Errore: ${error.message}`, 'error');
  }
};

 // Handle photo/sketch upload - Conversione in Base64 con scadenza 30 giorni
const handlePhotoUpload = async (e, type = 'photos', isClient = false) => {
  console.log('🔍 handlePhotoUpload chiamata!');
  const files = Array.from(e.target.files);
  
  // Data di scadenza: 30 giorni da oggi
  const expiryDate = new Date();
  expiryDate.setDate(expiryDate.getDate() + 30);
  
  // Converte ogni file in Base64 per renderlo persistente
  const photos = await Promise.all(files.map(file => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        resolve({
          id: Date.now() + Math.random(),
          url: event.target.result, // Base64 string persistente
          name: file.name,
          size: file.size,
          uploadDate: new Date().toISOString(),
          expiryDate: expiryDate.toISOString()
        });
      };
      reader.readAsDataURL(file);
    });
  }));
  
  if (isClient) {
    setClientFormData(prev => ({ ...prev, referencePhotos: [...prev.referencePhotos, ...photos] }));
  } else {
    setFormData(prev => ({ ...prev, [type]: [...prev[type], ...photos] }));
  }
  
  console.log('🔍 Foto convertite in Base64 e salvate!');
  openMessageBox(
    `${photos.length} foto caricata/e con successo!\n⚠️ Le immagini verranno eliminate automaticamente dopo 30 giorni (${expiryDate.toLocaleDateString('it-IT')}).`, 
    'success'
  );
};

  // Remove a photo/sketch
  const removePhoto = (photoId, type = 'photos', isClient = false) => {
    if (isClient) {
      setClientFormData(prev => ({
        ...prev,
        referencePhotos: prev.referencePhotos.filter(p => p.id !== photoId)
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [type]: prev[type].filter(p => p.id !== photoId)
      }));
    }
  };

 // Stato per avvisi mensili
 const [monthlyAlerts, setMonthlyAlerts] = useState([]);
 const [dismissedMonthlyAlerts, setDismissedMonthlyAlerts] = useState(new Set());

 // Funzione per controllare avvisi mensili
 const checkMonthlyAlerts = useCallback(() => {
   const now = new Date();
   const alerts = appointments.filter(apt => {
     const aptDate = new Date(apt.date);
     const diffDays = (now - aptDate) / (1000 * 60 * 60 * 24);
     return diffDays >= 30 && !dismissedMonthlyAlerts.has(apt.id);
   });
   setMonthlyAlerts(alerts);
 }, [appointments, dismissedMonthlyAlerts]);

 useEffect(() => {
  // Ricalcola sempre; se non ci sono appuntamenti, la lista diventa vuota
  checkMonthlyAlerts();
}, [appointments, checkMonthlyAlerts]);

// Funzione per archiviare un avviso mensile
const dismissAlert = async (alertId) => {
  const updated = new Set([...dismissedMonthlyAlerts, alertId]);
  setDismissedMonthlyAlerts(updated);
  localStorage.setItem('dismissedMonthlyAlerts', JSON.stringify([...updated]));

  if (user) {
    await updateDoc(doc(db, 'users', user.uid), {
      dismissedMonthlyAlerts: Array.from(updated)
    });
  }
};

// Rimuove automaticamente le foto scadute
const cleanExpiredPhotos = () => {
  const now = new Date();
  
  // Pulisci foto negli appuntamenti
  setAppointments(prevAppointments => 
    prevAppointments.map(apt => ({
      ...apt,
      photos: apt.photos?.filter(photo => new Date(photo.expiryDate) > now) || [],
      sketches: apt.sketches?.filter(sketch => new Date(sketch.expiryDate) > now) || []
    }))
  );
  
  // Pulisci foto nei clienti
  setClients(prevClients =>
    prevClients.map(client => ({
      ...client,
      referencePhotos: client.referencePhotos?.filter(photo => new Date(photo.expiryDate) > now) || []
    }))
  );
  
  console.log('🗑️ Pulizia foto scadute completata');
};

// Apre l'immagine in un modal
const openImageModal = (imageUrl, imageName) => {
  setSelectedImage({ url: imageUrl, name: imageName });
  setShowImageModal(true);
};

  // Colori randomici per artisti, evitando verde e grigio
  const generateColorFromName = (name) => {
  const colors = [
    'bg-purple-600',
    'bg-pink-500',
    'bg-red-500',
    'bg-blue-500',
    'bg-yellow-500',
    'bg-indigo-500',
    'bg-orange-500'
  ];
  // Crea un hash dal nome
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
};

  const artistColors = useMemo(() => {
  const colorMap = {};
  if (Array.isArray(artists)) {
    artists.forEach((artist) => {
      const name = typeof artist === 'object' ? artist.name : artist;
      colorMap[name] = generateColorFromName(name);
    });
  }
  return colorMap;
}, [artists]);

  // Memoized filtered appointments for the appointments list view
  const filteredAppointments = useMemo(() => {
    let filtered = appointments;

    if (searchTerm) {
      filtered = filtered.filter(apt =>
        apt.clientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        apt.project.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (selectedArtist !== 'all') {
      filtered = filtered.filter(apt => apt.artist === selectedArtist);
    }

    return filtered;
  }, [appointments, searchTerm, selectedArtist]);

  // Group filtered appointments by month for the appointments list view
  const appointmentsByMonth = useMemo(() => {
    const grouped = {};
    filteredAppointments.forEach(apt => {
      const date = new Date(apt.date);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      if (!grouped[monthKey]) {
        grouped[monthKey] = [];
      }
      grouped[monthKey].push(apt);
    });
    return grouped;
  }, [filteredAppointments]);

  // Month names for display
  const monthNames = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
                      'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];
  // Day names for display
  const dayNames = ['Dom', 'Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab'];

  // Handle client selection in appointment form
  const handleClientSelect = (e) => {
    const selectedClientName = e.target.value;
    const selectedClient = clients.find(c => c.name === selectedClientName);
    if (selectedClient) {
      setFormData({
        ...formData,
        clientName: selectedClient.name,
        clientPhone: selectedClient.phone,
        clientEmail: selectedClient.email
      });
    } else {
      // If no client selected (e.g., initial state or empty option)
      setFormData({
        ...formData,
        clientName: '',
        clientPhone: '',
        clientEmail: ''
      });
    }
  };

  // Esporta i clienti in formato Excel (XLSX)
  const exportClientsToExcel = () => {
    if (!clients || clients.length === 0) {
      openMessageBox('Non ci sono clienti da esportare.', 'info');
      return;
    }

    const data = clients.map(client => ({
      Nome: client.name || '',
      Telefono: client.phone || '',
      Email: client.email || '',
      Note: client.notes || '',
      Allergie: client.allergies || '',
      'Zone Preferite': client.preferredZones || '',
      'Artista Preferito': client.preferredArtist || ''
    }));

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Clienti');

    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const file = new Blob([excelBuffer], { type: 'application/octet-stream' });
    saveAs(file, 'clienti.xlsx');
  };

  // (Optional debug)
  if (process.env.NODE_ENV !== 'production') {
    console.debug('[InkHub] UA=', navigator.userAgent);
  }


  if (loadingAuth) {
    return (
      <div className="min-h-screen bg-gray-900 text-gray-100 flex items-center justify-center">
        <div className="text-xl">Caricamento autenticazione...</div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-900 text-gray-100 flex items-center justify-center p-4">
        <div className="bg-gray-800 rounded-xl p-6 shadow-2xl max-w-md w-full border border-gray-700">
          <div className="text-center mb-8">
            <img 
              src="https://www.lateliertattoosupply.com/img/inkhub.png" 
              alt="InkHub Logo" 
              className="h-36 w-36 mx-auto mb-6 rounded-full p-2"
            />
            <h1 className="text-4xl font-bold mb-1 bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">InkHub</h1>
            <p className="text-gray-400">Sistema di gestione professionale per studi tattoo</p>
          </div>
          <h2 className="text-2xl font-semibold mb-6 text-white text-center">Accedi</h2>
          <div className="mb-6 rounded-lg border border-purple-500/40 bg-purple-950/40 p-4 text-sm text-gray-200">
            <p className="font-semibold text-purple-300">Credenziali di prova</p>
            <p>Email: {DEMO_LOGIN_EMAIL}</p>
            <p>Password: {DEMO_LOGIN_PASSWORD}</p>
          </div>
          {/* Login Form */}
          <form onSubmit={handleLogin} className="space-y-4 mb-8">
            <h3 className="text-xl font-semibold text-purple-400">Login</h3>
            <div>
              <label htmlFor="loginEmail" className="block text-sm font-medium text-gray-300 mb-1">Email</label>
              <input
                type="email"
                id="loginEmail"
                value={loginEmail}
                onChange={(e) => setLoginEmail(e.target.value)}
                className="w-full px-3 py-2 bg-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                placeholder={DEMO_LOGIN_EMAIL}
                required
              />
            </div>
            <div>
              <label htmlFor="loginPassword" className="block text-sm font-medium text-gray-300 mb-1">Password</label>
              <input
                type="password"
                id="loginPassword"
                value={loginPassword}
                onChange={(e) => setLoginPassword(e.target.value)}
                className="w-full px-3 py-2 bg-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                placeholder={DEMO_LOGIN_PASSWORD}
                required
              />
            </div>
            <button
              type="submit"
              className="w-full bg-purple-600 hover:bg-purple-700 px-4 py-2 rounded-lg transition-colors font-semibold"
            >
              Accedi
            </button>
          </form>
          <p className="text-sm text-gray-400 text-center">
            La registrazione è disattivata in questa versione demo.
          </p>
          {/* Termini e Privacy */}
          <p className="text-xs text-gray-400 text-center mt-6">
            Utilizzando questa app accetti i{" "}
            <span className="underline">
              Termini di Servizio
            </span>{" "}
            e la nostra{" "}
            <span className="underline">
              Privacy Policy
            </span>.
          </p>
        </div>
        <MessageBox
          message={messageBoxMessage}
          type={messageBoxType}
          onClose={closeMessageBox}
          show={showMessageBox}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100 p-4 font-inter">
      <div className="max-w-7xl mx-auto">
        <header className="mb-8 flex items-start sm:items-center justify-between flex-wrap gap-4">
          <div className="inkhub-brand flex items-center gap-3 flex-1 min-w-0">
            <div className="w-16 h-16 rounded-full overflow-hidden p-2 lg:w-24 lg:h-24">
  <img
    src="https://www.lateliertattoosupply.com/img/inkhub.png"
    alt="InkHub Logo"
    className="w-full h-full object-cover rounded-full"
  />
</div>
            <div className="min-w-0">
              <h1 className="text-2xl lg:text-4xl font-bold leading-tight bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                InkHub
              </h1>
              <p className="text-sm lg:text-base text-gray-400 truncate">Il sistema di gestione intelligente per il tuo Studio</p>
            </div>
          </div>
          <div className="inkhub-actions flex items-center gap-2 sm:gap-4 order-2 sm:order-none w-auto sm:w-auto justify-start sm:justify-end flex-nowrap">
            <button
              onClick={() => {
                setEditingStudioName(userStudioName);
                setEditingStudioPhone(userStudioPhone);
                setShowStudioSettings(true);
              }}
              className="bg-gray-700 hover:bg-gray-600 text-gray-300 hover:text-white px-3 py-2 rounded-lg transition-colors text-lg"
              title="Impostazioni Studio"
            >
              ⚙️
            </button>
            <button
              onClick={() => window.open('/guida.html', '_blank', 'noopener,noreferrer')}
              className="bg-purple-600 hover:bg-purple-700 text-white px-3 py-2 rounded-lg transition-colors text-sm"
              title="Apri Tutorial"
            >
              Tutorial
            </button>
            <button
  onClick={handleLogout}
  title="Logout"
  className="bg-gray-700 hover:bg-gray-600 text-gray-300 hover:text-white px-4 py-2 rounded-lg transition-colors inline-flex items-center gap-2 text-sm whitespace-nowrap"
>
  <User className="w-4 h-4" />
  <span>Logout</span>
</button>
            {studioLogo && (
              <img
                src={studioLogo}
                alt="Logo Studio"
                className="rounded-full w-14 h-14 sm:w-16 sm:h-16 lg:w-20 lg:h-20 object-cover border-2 sm:border-4 border-purple-400"
              />
            )}
          </div>
        </header>




        {/* Avvisi mensili post-tatuaggio */}
        {/* Modale reminder appuntamenti in scadenza */}
        {/* eslint-disable-next-line no-unused-expressions */}
        {showReminderAlert && pendingReminders.length > 0 && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-800 rounded-xl p-6 shadow-2xl max-w-md w-full border border-gray-700">
              <h3 className="text-xl font-semibold text-yellow-400 mb-4 flex items-center gap-2">
                <Clock className="w-6 h-6" /> Reminder Appuntamenti
              </h3>
              <p className="text-gray-200 mb-4">
                Hai {pendingReminders.length} appuntamento{pendingReminders.length > 1 ? 'i' : ''} confermat{pendingReminders.length > 1 ? 'i' : 'o'} nelle prossime 48 ore senza reminder inviato. Vuoi inviare ora un promemoria email?
              </p>
              <ul className="mb-4 text-sm text-gray-300">
                {pendingReminders.map(apt => (
                  <li key={apt.id} className="mb-1">
                    <span className="font-semibold">{apt.clientName}</span> – {apt.project} ({new Date(apt.date).toLocaleDateString('it-IT')}, {apt.time})
                  </li>
                ))}
              </ul>
              <div className="flex flex-col sm:flex-row gap-2 justify-end mt-4">
                <button
                  onClick={() => setShowReminderAlert(false)}
                  className="px-4 py-2 bg-gray-700 text-gray-200 rounded hover:bg-gray-600"
                >
                  Annulla
                </button>
                <button
                  onClick={sendBulkReminders}
                  className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-500"
                >
                  Invia Reminder
                </button>
                <button
                  onClick={snoozeReminders}
                  className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-500"
                >
                  Ricorda tra 2h
                </button>
              </div>
            </div>
          </div>
        )}
        {/* Titolo calendario disattivato nelle viste day, month e week per evitare duplicazione */}
        {notificationsEnabled && monthlyAlerts.length > 0 && (
  <div className="bg-gray-800 border-l-4 border-yellow-500 text-white p-4 rounded-lg mb-6 shadow-lg">
    <h3 className="text-lg font-semibold mb-2 text-yellow-400">📢 Promemoria post-tatuaggio:</h3>
    {monthlyAlerts.map(alert => (
      <div
        key={alert.id}
        className="flex flex-col sm:flex-row sm:items-center justify-between bg-gray-700 p-3 rounded mb-2"
      >
        <span className="mb-2 sm:mb-0">
          <strong className="text-yellow-400">{alert.artist}</strong>, è passato un mese da quando hai tatuato{" "}
          <strong className="text-yellow-400">{alert.clientName}</strong>, che ne dici di chiedergli come sta andando?
        </span>
        <div className="flex gap-2">
          {alert.clientPhone && (
            <a
              href={`https://wa.me/${alert.clientPhone.replace(/\D/g, '')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded text-sm"
            >
              WhatsApp
            </a>
          )}
          <button
            onClick={() => dismissAlert(alert.id)}
            className="bg-gray-600 hover:bg-gray-700 text-white px-3 py-1 rounded text-sm"
          >
            Archivia
          </button>
        </div>
      </div>
    ))}
  </div>
)}

          {showStudioSettings && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-800 rounded-xl p-6 shadow-2xl max-w-md w-full border border-gray-700 max-h-[80vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-semibold text-white flex items-center gap-2">
                  <Palette className="w-6 h-6 text-purple-400" />
                  Impostazioni Studio
                </h3>
                <button
                  onClick={() => setShowStudioSettings(false)}
                  className="p-2 rounded-full hover:bg-gray-700 text-gray-400 hover:text-white transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              {/* Nome Studio */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-300 mb-1">Nome Studio</label>
                <input
                  type="text"
                  value={editingStudioName}
                  onChange={(e) => setEditingStudioName(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
              {/* Modalità test invio email / Stato Gmail */}
              {/* Telefono Studio */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-300 mb-1">Telefono Studio</label>
                <input
                  type="tel"
                  value={editingStudioPhone}
                  onChange={(e) => setEditingStudioPhone(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
              <StudioLogoUploader logo={studioLogo} onChangeLogo={setStudioLogo} />

              {/* Toggle per notifiche mensili */}
              <div className="flex items-center justify-between mb-4">
                <label className="text-sm font-medium text-gray-300">Promemoria mensili post-tatuaggio</label>
                <button
                  onClick={() => setNotificationsEnabled(!notificationsEnabled)}
                  className={`px-3 py-1 rounded-lg text-white ${notificationsEnabled ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}`}
                >
                  {notificationsEnabled ? 'Attive' : 'Disattive'}
                </button>
              </div>
              {/* Bottone collegamento Gmail */}
              {gmailToken ? (
                <>
                  <button
                    type="button"
                    className="w-full mb-2 bg-gray-600 text-white px-4 py-2 rounded-lg opacity-70 cursor-not-allowed"
                    aria-disabled="true"
                  >
                    Scollega account Gmail
                  </button>
                  <p className="text-green-400 text-sm mt-1">
                    ✅ Gmail collegato{gmailEmail ? `: ${gmailEmail}` : ''}. Le email verranno inviate dal tuo account Gmail.
                  </p>
                </>
              ) : (
                <button
                  type="button"
                  className="w-full mb-4 bg-red-600 text-white px-4 py-2 rounded-lg opacity-70 cursor-not-allowed"
                  aria-disabled="true"
                >
                  Collega il tuo account Gmail
                </button>
              )}

              {/* Bottoni */}
<div className="flex justify-end gap-3 mt-6">
  <button
    type="button"
    onClick={(e) => {
      e.preventDefault();
      e.stopPropagation();
      setShowStudioSettings(false);
    }}
    className="px-4 py-2 bg-gray-700 text-gray-200 rounded-lg hover:bg-gray-600 transition-colors"
  >
    Annulla
  </button>

  <button
    type="button"
    onClick={(e) => {
      e.preventDefault();
      e.stopPropagation();
      handleUpdateStudioSettings();
    }}
    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
  >
    Salva
  </button>
</div>
            </div>
          </div>
        )}
        {/* Modale reminder invio multiplo */}
        {showReminderModal && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-800 rounded-xl p-5 shadow-2xl w-full max-w-4xl border border-gray-700 max-h-[85vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-semibold text-white">Invia promemoria (48h)</h3>
                <button
                  onClick={() => setShowReminderModal(false)}
                  className="p-2 rounded-full hover:bg-gray-700 text-gray-400 hover:text-white transition-colors"
                  aria-label="Chiudi"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Colonna sinistra: lista clienti */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <button onClick={selectAllReminders} className="px-3 py-1 bg-gray-700 rounded text-sm">Seleziona tutti</button>
                    <button onClick={deselectAllReminders} className="px-3 py-1 bg-gray-700 rounded text-sm">Deseleziona tutti</button>
                  </div>

                  <div className="space-y-2">
                    {reminderCandidates.map((apt) => (
                      <label key={apt.id} className="flex items-start gap-3 bg-gray-700 rounded-lg p-2 cursor-pointer">
                        <input
                          type="checkbox"
                          className="mt-1"
                          checked={selectedReminderIds.has(apt.id)}
                          onChange={() => toggleReminderSelection(apt.id)}
                        />
                        <div className="text-sm text-gray-200">
                          <div className="font-semibold text-white">{apt.clientName}</div>
                          <div className="opacity-80">{new Date(apt.date).toLocaleDateString('it-IT')} • {apt.time} • {apt.artist}</div>
                          <div className="opacity-60">{apt.clientEmail}</div>
                          <button type="button" className="text-xs underline opacity-80" onClick={() => setPreviewApt(apt)}>
                            Anteprima
                          </button>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Colonna destra: anteprima email */}
                <div className="bg-gray-700 rounded-lg p-3 overflow-auto">
                  <h4 className="text-white font-semibold mb-2">Anteprima email</h4>
                  <div
                    className="bg-white rounded-md p-3 text-black"
                    dangerouslySetInnerHTML={{
                      __html: previewApt ? buildReminderEmailHTML(previewApt) : '<p style="margin:0">Seleziona un appuntamento a sinistra…</p>',
                    }}
                  />
                </div>
              </div>

              <div className="flex justify-end gap-2 mt-4">
                <button
                  onClick={() => setShowReminderModal(false)}
                  className="px-4 py-2 bg-gray-700 text-gray-200 rounded hover:bg-gray-600"
                >
                  Annulla
                </button>
                <button
                  onClick={sendSelectedReminders}
                  className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-500"
                >
                  Invia i reminder selezionati
                </button>
              </div>
            </div>
          </div>
        )}
        <div
  className="mb-6 bg-gradient-to-r from-purple-900 to-pink-900 rounded-lg p-3 sm:p-4 relative overflow-hidden"
  data-promo-hero="1"
>
  <div className="absolute inset-0 bg-black opacity-50"></div>
  <div className="relative z-10 flex flex-col sm:flex-row items-center justify-between text-center sm:text-left">
    <div>
      <h3 className="text-base sm:text-lg font-semibold mb-1 leading-tight sm:leading-snug">
        🎨 Tutto per il tuo Studio Tattoo
      </h3>
      <p className="text-xs sm:text-sm text-gray-300 leading-snug sm:leading-normal">
        Scopri l'Atelier Tattoo Supply - Qualità professionale per artisti professionisti
      </p>
    </div>
    <span
      className="mt-3 sm:mt-0 bg-white/80 text-gray-900 text-xs sm:text-sm px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg font-medium whitespace-nowrap cursor-not-allowed"
      aria-disabled="true"
    >
      Visita lo Shop
    </span>
  </div>
</div>

        {/* Navigation tabs */}
        <nav className="flex gap-2 sm:gap-4 mb-8 flex-wrap">
          <button
            onClick={() => setView('calendar')}
            className={`px-3 py-2 sm:px-4 sm:py-2 rounded-lg transition-all flex items-center justify-center text-sm sm:text-base ${
              view === 'calendar'
                ? 'bg-purple-600 text-white'
                : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
            }`}
          >
            <Calendar className="inline-block w-4 h-4 mr-1 sm:mr-2" />
            Calendario
          </button>
          <button
            onClick={() => setView('appointments')}
            className={`px-3 py-2 sm:px-4 sm:py-2 rounded-lg transition-all flex items-center justify-center text-sm sm:text-base ${
              view === 'appointments'
                ? 'bg-purple-600 text-white'
                : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
            }`}
          >
            <Clock className="inline-block w-4 h-4 mr-1 sm:mr-2" />
            Appuntamenti
          </button>
          <button
            onClick={() => setView('clients')}
            className={`px-3 py-2 sm:px-4 sm:py-2 rounded-lg transition-all flex items-center justify-center text-sm sm:text-base ${
              view === 'clients'
                ? 'bg-purple-600 text-white'
                : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
            }`}
          >
            <User className="inline-block w-4 h-4 mr-1 sm:mr-2" />
            Clienti
          </button>
          <button
            onClick={() => setView('stats')}
            className={`px-3 py-2 sm:px-4 sm:py-2 rounded-lg transition-all flex items-center justify-center text-sm sm:text-base ${
              view === 'stats'
                ? 'bg-purple-600 text-white'
                : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
            }`}
          >
            <BarChart2 className="inline-block w-4 h-4 mr-1 sm:mr-2" />
            Statistiche
          </button>
<button
  onClick={() => setView('artists')}
  className={`px-3 py-2 sm:px-4 sm:py-2 rounded-lg transition-all flex items-center justify-center text-sm sm:text-base ${
    view === 'artists'
      ? 'bg-purple-600 text-white'
      : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
  }`}
>
  <Users className="inline-block w-4 h-4 mr-1 sm:mr-2" />
  Artisti
</button>
<button
  onClick={() => setView('materials')}
  className={`px-3 py-2 sm:px-4 sm:py-2 rounded-lg transition-all flex items-center justify-center text-sm sm:text-base ${
  view === 'materials'
    ? 'bg-purple-600 text-white'
    : 'bg-gray-800 text-orange-400 hover:bg-gray-700'
}`}
>
  <Package className="inline-block w-4 h-4 mr-1 sm:mr-2" />
  Ordina Materiale
</button>
          <button
            onClick={() => setView('links')}
            className={`px-3 py-2 sm:px-4 sm:py-2 rounded-lg transition-all flex items-center justify-center text-sm sm:text-base ${
              view === 'links'
                ? 'bg-purple-600 text-white'
                : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
            }`}
          >
            <Link2 className="inline-block w-4 h-4 mr-1 sm:mr-2" />
            Link Utili
          </button>
        </nav>

        {/* Artist filter */}
        {view !== 'links' && view !== 'materials' && (
          <div className="mb-4">
            <select
  value={selectedArtist}
  onChange={(e) => setSelectedArtist(e.target.value)}
  className="bg-gray-800 text-white px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
>
  <option value="all">Tutti gli artisti</option>
  {Array.isArray(artists) && artists.map((artist, index) => (
    <option key={artist.id || index} value={artist.name || artist}>{artist.name || artist}</option>
  ))}
</select>
          </div>
        )}

        {/* Calendar View */}
        {view === 'calendar' && (
          <div className="bg-gray-800 rounded-xl p-4 sm:p-6 shadow-xl">
            {/* Pulsanti vista mese, settimana, giorno */}
            <div className="flex justify-center gap-2 mb-4 mt-2">
  <button
    className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
      calendarView === 'month' ? 'bg-purple-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
    }`}
    onClick={() => setCalendarView('month')}
  >
    Mese
  </button>
  <button
    className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
      calendarView === 'week' ? 'bg-purple-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
    }`}
    onClick={() => setCalendarView('week')}
  >
    Settimana
  </button>
  <button
    className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
      calendarView === 'day' ? 'bg-purple-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
    }`}
    onClick={() => setCalendarView('day')}
  >
    Giorno
  </button>
</div>
{/* FINE: Pulsanti vista mese, settimana, giorno */}

            <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
              <button
                onClick={handlePrevPeriod}
                className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <div className="flex flex-col sm:flex-row items-center gap-4">
                <h2 className="text-xl sm:text-2xl font-semibold text-center">
                  {calendarView === 'month'
                    ? `${monthNames[selectedDate.getMonth()]} ${selectedDate.getFullYear()}`
                    : `Settimana del ${selectedDate.getDate()} ${monthNames[selectedDate.getMonth()]}`
                  }
                </h2>
                
              </div>
              <button
                onClick={handleNextPeriod}
                className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>

            {calendarView === 'month' && (
              <div className="grid grid-cols-7 gap-2">
                {dayNames.map(day => (
                  <div key={day} className="text-center text-sm font-semibold text-gray-400 py-2">
                    {day}
                  </div>
                ))}

                {getDaysInMonth(selectedDate).map((dayObj, index) => {
                  const dayAppointments = getAppointmentsForDate(dayObj.date);
                  const isToday = formatDate(dayObj.date) === formatDate(new Date());

                  return (
                    <div
                      key={index}
                      onClick={() => dayObj.currentMonth && handleDateClick(dayObj.date)}
                      className={`
                        border border-gray-700 rounded-lg p-2 min-h-[80px] cursor-pointer
                        transition-all hover:border-purple-500
                        ${!dayObj.currentMonth ? 'bg-gray-900 text-gray-600' : 'bg-gray-800'}
                        ${isToday ? 'ring-2 ring-purple-500' : ''}
                      `}
                    >
                      <div className="text-sm font-medium mb-1">{dayObj.date.getDate()}</div>
                      {dayAppointments.length > 0 && (
                        <div className="space-y-1">
                          {dayAppointments.slice(0, 2).map((apt, i) => (
                            <div
                              key={i}
                              className={`text-xs ${artistColors[apt.artist] || 'bg-purple-600'} rounded px-1 py-0.5 truncate`}
                              title={`${apt.time} - ${apt.clientName} (${apt.artist})`}
                            >
                              {apt.time} {apt.clientName.split(' ')[0]}
                            </div>
                          ))}
                          {dayAppointments.length > 2 && (
                            <div className="text-xs text-gray-400">
                              +{dayAppointments.length - 2} altri
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            {calendarView === 'week' && (
  <div className="relative overflow-x-auto">
    <div className="grid grid-cols-8 gap-px min-w-[1000px]">
      <div className="bg-gray-800 text-xs text-gray-400 py-2 px-1 sticky left-0 z-10">
        Ora
      </div>
      {getWeekDays(selectedDate).map((day, i) => {
        const isToday = formatDate(day) === formatDate(new Date());
        return (
          <div
            key={i}
            className={`bg-gray-800 text-xs text-center py-2 px-1 sticky top-0 z-10 ${
              isToday ? 'text-purple-400 font-semibold' : 'text-gray-400'
            }`}
          >
            <div>{dayNames[day.getDay()]}</div>
            <div className="text-base">{day.getDate()}</div>
          </div>
        );
      })}
    </div>

    <div className="relative">
      {Array.from({ length: 14 }, (_, i) => i + 8).map((hour) => (
        <div key={hour} className="grid grid-cols-8 gap-px h-16 border-t border-gray-700">
          <div className="bg-gray-900 text-xs text-gray-500 py-1 px-1 sticky left-0 z-10">
            {String(hour).padStart(2, '0')}:00
          </div>
          {getWeekDays(selectedDate).map((day, colIndex) => {
            const appointmentsAtHour = getAppointmentsForDate(day).filter((apt) => {
              return parseInt(apt.time.split(':')[0], 10) === hour;
            });

            return (
              <div
                key={colIndex}
                className="relative bg-gray-900 hover:bg-gray-800 cursor-pointer"
                onClick={() => handleDateClick(day)}
              >
                {appointmentsAtHour.map((apt, index) => {
  const height = apt.duration * 64;
  const width = 100 / appointmentsAtHour.length;
  const left = index * width;

  return (
    <div
      key={index}
      className={`absolute rounded px-1 py-1 text-xs text-white ${apt.colorClass}`}
      style={{
        top: 0,
        left: `${left}%`,
        width: `${width}%`,
        height: `${height}px`,
        opacity: 0.7,
        zIndex: 10 + index,
      }}
    >
      <div className="font-semibold truncate text-sm">{apt.clientName}</div>
    </div>
  );
})}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  </div>
)}
            {calendarView === 'day' && (
              <div className="bg-gray-700 rounded-lg p-4 text-white">
                <h2 className="text-lg font-semibold mb-4 text-center">
  {calendarView === 'day'
    ? selectedDate.toLocaleDateString('it-IT', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      })
    : `Settimana del ${selectedDate.toLocaleDateString('it-IT', {
        day: 'numeric',
        month: 'long'
      })}`}
</h2>
                <div className="relative">
                  {/* Timeline oraria */}
                  <div className="border-l border-gray-600 h-[1120px] relative ml-12">
                    {[...Array(14)].map((_, i) => {
                      const hour = i + 8;
                      return (
                        <div
                          key={hour}
                          className="absolute left-[-3rem] w-full flex items-start"
                          style={{ top: `${(hour - 8) * 80}px` }}
                        >
                          <div className="w-12 text-right pr-2 text-xs text-gray-400">
                            {String(hour).padStart(2, '0')}:00
                          </div>
                          <div className="flex-1 border-t border-gray-600 h-0 w-full"></div>
                        </div>
                      );
                    })}

                    {/* Appuntamenti con gestione sovrapposizioni */}
                    {(() => {
  const appointments = getAppointmentsForDate(selectedDate);

  return appointments.map((apt, i) => {
    const [hour, minute] = apt.time.split(':').map(Number);
    const start = hour * 60 + minute;
    const top = ((hour - 8) * 80) + (minute / 60) * 80;
    const height = (apt.duration || 2) * 80;

    // Trova quanti si sovrappongono
    const overlapping = appointments.filter((other, j) => {
      if (i === j) return false;
      const [oh, om] = other.time.split(':').map(Number);
      const otherStart = oh * 60 + om;
      const otherEnd = otherStart + (other.duration || 2) * 60;
      const aptEnd = start + (apt.duration || 2) * 60;
      return otherStart < aptEnd && start < otherEnd;
    });

    const overlaps = overlapping.length;
    const totalOverlap = overlaps + 1;
    const widthPercent = 100 / totalOverlap;
    const leftOffset = (i % totalOverlap) * widthPercent;

    return (
      <div
  key={i}
  className={`inkhub-day-card absolute rounded-md px-3 py-2 shadow-md text-white ${artistColors[apt.artist] || 'bg-purple-600'} bg-opacity-70 overflow-hidden flex flex-col gap-0.5 cursor-pointer`}
  style={{
    top: `${top}px`,
    height: `${height}px`,
    width: `calc(${widthPercent}% - 6px)`,
    left: `calc(${leftOffset}% + 12px)`,
    fontSize: '0.875rem',
    lineHeight: '1.25rem',
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    overflowWrap: 'anywhere',
    hyphens: 'auto',
    boxSizing: 'border-box'
  }}
  role="button"
  tabIndex={0}
  onClick={() => {
    // Apri il MODALE DI MODIFICA
    setEditingAppointment(apt);        // passa l'appuntamento corrente
    setShowAppointmentForm(true);      // mostra il form/modale di modifica
  }}
  onKeyDown={(e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setEditingAppointment(apt);
      setShowAppointmentForm(true);
    }
  }}
>
        <div style={{fontWeight: 700}}>{apt.time} – {apt.duration}h</div>

        {/* Nome cliente: max 2 righe */}
        <div
          style={{
            fontSize: '0.95rem',
            fontWeight: 700,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}
        >
          {apt.clientName}
        </div>

        {/* Progetto */}
        <div style={{fontSize: '0.8rem', opacity: 0.9}}>{apt.project}</div>

        {/* Artista */}
        <div style={{fontStyle: 'italic', fontSize: '0.8rem', opacity: 0.85}}>{apt.artist}</div>
      </div>
    );
  });
})()}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

{view === 'materials' && (
  <div className="bg-gray-800 rounded-xl p-4 sm:p-6 shadow-xl">
    <h2 className="text-2xl font-semibold mb-6 text-orange-400">📦 Ordina Materiale</h2>
    <div className="space-y-4 mb-8">
      <div className="bg-gray-700 rounded-lg p-4 flex items-center gap-4 opacity-80">
        <MessageCircle className="w-8 h-8 text-green-400" />
        <div>
          <p className="text-lg font-semibold">Scrivici l'ordine</p>
          <p className="text-sm text-gray-400">Contattaci direttamente su WhatsApp</p>
        </div>
      </div>
      
      <div className="bg-gray-700 rounded-lg p-4 flex items-center gap-4 opacity-80">
        <Phone className="w-8 h-8 text-blue-400" />
        <div>
          <p className="text-lg font-semibold">Ordina per Telefono!</p>
          <p className="text-sm text-gray-400">Chiamaci direttamente al 334 394 0533</p>
        </div>
      </div>
      
      <div className="bg-gray-700 rounded-lg p-4 flex items-center gap-4 opacity-80">
        <ShoppingCart className="w-8 h-8 text-orange-400" />
        <div>
          <p className="text-lg font-semibold">Ordina sul sito in autonomia</p>
          <p className="text-sm text-gray-400">Visita il nostro shop online completo</p>
        </div>
      </div>
    </div>
    
    <div className="flex justify-center mt-8">
      <img 
        src="https://www.lateliertattoosupply.com/img/Atelier-logo-CittaND-.com-WH.png" 
        alt="L'Atelier Tattoo Supply" 
        className="h-24 opacity-90"
      />
    </div>
  </div>
)}

        {/* Appointments View */}
        {view === 'appointments' && (
          <div className="bg-gray-800 rounded-xl p-4 sm:p-6 shadow-xl">
            <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
              <div className="flex items-center gap-4">
                <h2 className="text-2xl font-semibold">Appuntamenti</h2>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      const newDate = new Date(appointmentMonth);
                      newDate.setMonth(newDate.getMonth() - 1);
                      setAppointmentMonth(newDate);
                    }}
                    className="p-1 hover:bg-gray-700 rounded"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <span className="text-purple-400">
                    {monthNames[appointmentMonth.getMonth()]} {appointmentMonth.getFullYear()}
                  </span>
                  <button
                    onClick={() => {
                      const newDate = new Date(appointmentMonth);
                      newDate.setMonth(newDate.getMonth() + 1);
                      setAppointmentMonth(newDate);
                    }}
                    className="p-1 hover:bg-gray-700 rounded"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Cerca appuntamenti..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="bg-gray-700 text-white pl-10 pr-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 w-full"
                  />
                </div>
                <div className="flex flex-col sm:flex-row gap-3">
  <button
    onClick={sendBulkReminders}
    className="bg-green-600 hover:bg-green-700 px-4 py-2 rounded-lg transition-colors flex items-center justify-center gap-2"
  >
    <Mail className="w-5 h-5" />
    Invia Reminder
  </button>
  <button
    onClick={() => setShowAppointmentForm(true)}
    className="bg-purple-600 hover:bg-purple-700 px-4 py-2 rounded-lg transition-colors flex items-center justify-center gap-2"
  >
    <Plus className="w-5 h-5" />
    Nuovo Appuntamento
  </button>
</div>
              </div>
            </div>

            {(() => {
              const currentMonthKey = `${appointmentMonth.getFullYear()}-${String(appointmentMonth.getMonth() + 1).padStart(2, '0')}`;
              const monthAppointments = appointmentsByMonth[currentMonthKey] || [];

              return (
                <div className="space-y-4">
                  {monthAppointments.length === 0 ? (
                    <div className="text-center py-8 text-gray-400">
                      Nessun appuntamento per questo mese
                    </div>
                  ) : (
                    monthAppointments.sort((a, b) => {
                      const dateA = new Date(`${a.date}T${a.time}`);
                      const dateB = new Date(`${b.date}T${b.time}`);
                      return dateA - dateB;
                    }).map(appointment => (
                      <div key={appointment.id} className="bg-gray-700 rounded-lg p-4 hover:bg-gray-600 transition-colors">
                        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center">
                          <div className="flex-1 w-full">
                            <div className="flex flex-wrap items-center gap-3 mb-2">
                              <h3 className="text-lg font-semibold">{appointment.clientName}</h3>
                              <span className="text-sm text-gray-400">
                                {new Date(appointment.date).toLocaleDateString('it-IT')} - {appointment.time}
                              </span>
                              <span className={`text-sm px-2 py-1 rounded text-white ${artistColors[appointment.artist] || artistColors.default}`}>
                                {appointment.artist}
                              </span>
                            </div>
                            <p className="text-purple-400 mb-2">{appointment.project}</p>
                            {appointment.notes && (
                              <p className="text-sm text-gray-300 mb-2">📝 {appointment.notes}</p>
                            )}
                            <div className="flex flex-wrap gap-4 text-sm text-gray-400 mb-2">
                              <span>⏱️ {appointment.duration}h</span>
                              <span>💰 Acconto: €{appointment.deposit}</span>
                              <span>💵 Totale: €{appointment.totalPrice}</span>
                              <span className={`px-2 py-1 rounded text-xs text-white ${
                                appointment.status === 'confirmed' ? 'bg-green-600' :
                                appointment.status === 'pending' ? 'bg-yellow-600' :
                                'bg-red-600'
                              }`}>
                                {appointment.status === 'confirmed' ? 'Confermato' :
                                  appointment.status === 'pending' ? 'In attesa' :
                                  'Cancellato'}
                              </span>
                            </div>
                            <div className="flex flex-wrap gap-4 mt-2">
                              {appointment.photos && appointment.photos.length > 0 && (
                                <div className="flex items-center gap-2">
                                  <Image className="w-4 h-4 text-gray-400" />
                                  <span className="text-sm text-gray-400">{appointment.photos.length} foto</span>
                                </div>
                              )}
                              {appointment.sketches && appointment.sketches.length > 0 && (
                                <div className="flex items-center gap-2">
                                  <FileText className="w-4 h-4 text-gray-400" />
                                  <span className="text-sm text-gray-400">{appointment.sketches.length} bozze</span>
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="flex gap-2 mt-4 sm:mt-0">
                            <button
                              onClick={() => {
  setEditingAppointment(appointment);
  setShowAppointmentForm(true);
}}
                              className="p-2 hover:bg-gray-600 rounded-lg transition-colors text-blue-400"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
  onClick={() => {
    openConfirmModal(
      'Sei sicuro di voler eliminare questo appuntamento?',
      async () => {
        try {
          await deleteDoc(doc(db, `users/${user.uid}/appointments`, appointment.id));
          openMessageBox('Appuntamento eliminato con successo!', 'success');
          window.location.reload();
        } catch (error) {
          openMessageBox(`Errore: ${error.message}`, 'error');
        }
      }
    );
  }}
  className="p-2 hover:bg-gray-600 rounded-lg transition-colors text-red-400"
>
<Trash2 className="w-4 h-4" />
</button>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              );
            })()}
          </div>
        )}

        {/* Clients View */}
        {view === 'clients' && (
          <div className="bg-gray-800 rounded-xl p-4 sm:p-6 shadow-xl">
            <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
              <h2 className="text-2xl font-semibold">Clienti</h2>
              <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto">
                <div className="relative w-full sm:w-auto">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Cerca clienti..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full px-3 py-2 bg-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>
                <button
                  onClick={() => setShowClientForm(true)}
                  className="bg-purple-600 hover:bg-purple-700 px-4 py-2 rounded-lg transition-colors flex items-center justify-center gap-2 w-full sm:w-auto"
                >
                  <Plus className="w-5 h-5" />
                  Nuovo Cliente
                </button>
  <button
    onClick={exportClientsToExcel}
    className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
  >
    📥 Esporta Clienti in Excel
  </button>
</div>
</div>

            <div className="space-y-4">
              {clients.filter(client =>
                client.name.toLowerCase().includes(searchTerm.toLowerCase()) &&
                (selectedArtist === 'all' || client.preferredArtist === selectedArtist)
              ).length === 0 ? (
                <div className="text-center py-8 text-gray-400">
                  Nessun cliente trovato
                </div>
              ) : (
                clients.filter(client =>
                  client.name.toLowerCase().includes(searchTerm.toLowerCase()) &&
                  (selectedArtist === 'all' || client.preferredArtist === selectedArtist)
                ).map(client => (
                  <div key={client.id} className="bg-gray-700 rounded-lg p-4 hover:bg-gray-600 transition-colors">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center">
                      <div className="flex-1 w-full">
                        <h3 className="text-lg font-semibold mb-1">{client.name}</h3>
                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-300 mb-2">
                          <span className="flex items-center gap-1"><Phone className="w-4 h-4" /> {client.phone}</span>
                          {client.phone && (
  <a
    href={`https://wa.me/${client.phone.replace(/\D/g, '')}`}
    target="_blank"
    rel="noopener noreferrer"
    className="text-green-400 underline text-sm ml-2"
  >
    Scrivi WhatsApp
  </a>
)}
                          <span className="flex items-center gap-1"><Mail className="w-4 h-4" /> {client.email}</span>
                          {client.preferredArtist && <span className="flex items-center gap-1"><User className="w-4 h-4" /> Artista: {client.preferredArtist}</span>}
                        </div>
                        <p className="text-sm text-gray-400 mb-2">Sessioni totali: {client.totalSessions}</p>
                        {client.notes && <p className="text-sm text-gray-300 mb-1 flex items-center gap-1">📌 Note: {client.notes}</p>}
                        {client.allergies && <p className="text-sm text-gray-300 mb-1 flex items-center gap-1">⚠️ Allergie: {client.allergies}</p>}
                        {client.preferredZones && <p className="text-sm text-gray-300 mb-1 flex items-center gap-1">🎯 Zone preferite: {client.preferredZones}</p>}
                        {client.consent && <p className="text-sm text-green-400 flex items-center gap-1">✅ Consenso firmato il: {new Date(client.consentDate).toLocaleDateString('it-IT')}</p>}
                        {client.referencePhotos && client.referencePhotos.length > 0 && (
                          <div className="mt-2">
                            <span className="text-sm text-gray-400">Foto di riferimento:</span>
                            <div className="flex flex-wrap gap-2 mt-1">
                              {client.referencePhotos.map(photo => (
                                <img key={photo.id} src={photo.url} alt={photo.name} className="w-16 h-16 object-cover rounded-md" />
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="flex gap-2 mt-4 sm:mt-0">
                        <button
                          onClick={() => {
  setEditingClient(client);
  setShowClientForm(true);
}}
                          className="p-2 hover:bg-gray-600 rounded-lg transition-colors text-blue-400"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteClient(client.id)}
                          className="p-2 hover:bg-gray-600 rounded-lg transition-colors text-red-400"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* New Products Section in Clients View */}
            <div className="mt-8 bg-gray-700 rounded-lg p-4 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <ClipboardList className="w-8 h-8 text-purple-400" />
                <div>
                  <h3 className="text-lg font-semibold">Prodotti consigliati per i tuoi clienti</h3>
                  <p className="text-sm text-gray-400">Scopri la nostra selezione di prodotti aftercare e creme per la cura del tatuaggio</p>
                </div>
              </div>
              <span
                className="bg-purple-600/70 text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2 whitespace-nowrap cursor-not-allowed"
                aria-disabled="true"
              >
                Vedi Ultimi Arrivi
                <Package className="w-4 h-4" />
              </span>
            </div>
          </div>
        )}

        {/* Statistics View */}
        {view === 'stats' && (
          <div className="bg-gray-800 rounded-xl p-4 sm:p-6 shadow-xl">
            <h2 className="text-2xl font-semibold mb-6">Statistiche Mensili</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-gray-700 rounded-lg p-4 flex items-center gap-4">
                <Calendar className="w-8 h-8 text-purple-400" />
                <div>
                  <p className="text-sm text-gray-400">Appuntamenti del Mese</p>
                  <p className="text-2xl font-bold">{stats.monthlyAppointments}</p>
                </div>
              </div>
              <div className="bg-gray-700 rounded-lg p-4 flex items-center gap-4">
                <ShoppingBag className="w-8 h-8 text-green-400" />
                <div>
                  <p className="text-sm text-gray-400">Ricavo Totale (Mese)</p>
                  <p className="text-2xl font-bold">€{stats.totalRevenue.toFixed(2)}</p>
                </div>
              </div>
              <div className="bg-gray-700 rounded-lg p-4 flex items-center gap-4">
                <BookOpen className="w-8 h-8 text-blue-400" />
                <div>
                  <p className="text-sm text-gray-400">Acconti Raccolti (Mese)</p>
                  <p className="text-2xl font-bold">€{stats.totalDeposits.toFixed(2)}</p>
                </div>
              </div>
              <div className="bg-gray-700 rounded-lg p-4 flex items-center gap-4">
                <User className="w-8 h-8 text-pink-400" />
                <div>
                  <p className="text-sm text-gray-400">Clienti Totali</p>
                  <p className="text-2xl font-bold">{stats.totalClients}</p>
                </div>
              </div>
              <div className="bg-gray-700 rounded-lg p-4 flex items-center gap-4">
                <Plus className="w-8 h-8 text-yellow-400" />
                <div>
                  <p className="text-sm text-gray-400">Nuovi Clienti (Mese)</p>
                  <p className="text-2xl font-bold">{stats.newClients}</p>
                </div>
              </div>
            </div>

            <h3 className="text-xl font-semibold mt-8 mb-4">Statistiche per Artista (Mese Corrente)</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {Array.isArray(artists) && artists.map((artist, index) => {
  const artistName = typeof artist === 'object' ? artist.name : artist;
  return (
    <div key={artist.id || index} className="bg-gray-700 rounded-lg p-4">
      <h4 className="text-lg font-semibold text-purple-300 mb-2">{artistName}</h4>
      <p className="text-sm text-gray-300">Appuntamenti: {stats.artistStats[artistName]?.appointments || 0}</p>
      <p className="text-sm text-gray-300">Ricavo: €{(stats.artistStats[artistName]?.revenue || 0).toFixed(2)}</p>
    </div>
  );
})}
            </div>
          </div>
        )}

        {/* Useful Links View */}
        {view === 'links' && (
          <div className="bg-gray-800 rounded-xl p-4 sm:p-6 shadow-xl">
            <h2 className="text-2xl font-semibold mb-6">Link Utili</h2>
            <div className="space-y-4 mb-8">
              <div className="bg-gray-700 rounded-lg p-4 flex items-center gap-4 opacity-80">
                <ShoppingCart className="w-8 h-8 text-purple-400" />
                <div>
                  <p className="text-lg font-semibold">Acquista ora</p>
                  <p className="text-sm text-gray-400">Visita il nostro shop online completo</p>
                </div>
              </div>
              <div className="bg-gray-700 rounded-lg p-4 flex items-center gap-4 opacity-80">
                <Rss className="w-8 h-8 text-blue-400" />
                <div>
                  <p className="text-lg font-semibold">Blog</p>
                  <p className="text-sm text-gray-400">Guide, consigli e novità dal mondo del tattoo</p>
                </div>
              </div>
              <div className="bg-gray-700 rounded-lg p-4 flex items-center gap-4 opacity-80">
                <Star className="w-8 h-8 text-green-400" />
                <div>
                  <p className="text-lg font-semibold">Ultimi Arrivi</p>
                  <p className="text-sm text-gray-400">Scopri le ultime novità in catalogo</p>
                </div>
              </div>
              <div className="bg-gray-700 rounded-lg p-4 flex items-center gap-4 opacity-80">
                <MessageSquare className="w-8 h-8 text-teal-400" />
                <div>
                  <p className="text-lg font-semibold">Canale WhatsApp</p>
                  <p className="text-sm text-gray-400">Unisciti al nostro canale per offerte esclusive</p>
                </div>
              </div>
              <div className="bg-gray-700 rounded-lg p-4 flex items-center gap-4 opacity-80">
                <MailOpen className="w-8 h-8 text-red-400" />
                <div>
                  <p className="text-lg font-semibold">Scrivici</p>
                  <p className="text-sm text-gray-400">Contatto email disponibile su richiesta</p>
                </div>
              </div>
            </div>

            {/* Special Offer Banner */}
            <div className="bg-gradient-to-r from-pink-700 to-purple-700 rounded-lg p-5 text-center shadow-lg">
              <h3 className="text-xl font-bold text-white mb-2">🎉 Offerta Speciale per Utenti InkHub 🎉</h3>
              <p className="text-gray-200 mb-4">Usa il codice <span className="font-bold text-yellow-300 bg-black bg-opacity-30 px-2 py-1 rounded-md tracking-wider">INKHUB10</span> per uno sconto del 10% sul primo ordine!</p>
              <button
                onClick={() => {
                  navigator.clipboard.writeText('INKHUB10').then(() => {
                    openMessageBox('Codice INKHUB10 copiato!', 'success');
                  }).catch(err => {
                    openMessageBox('Impossibile copiare il codice, riprova manualmente.', 'error');
                  });
                }}
                className="bg-white text-purple-800 font-bold py-2 px-6 rounded-full hover:bg-gray-200 transition-colors shadow-md"
              >
                Applica Sconto
              </button>
            </div>
          </div>
        )}

{/* Artists View */}
        {view === 'artists' && (
          <div className="bg-gray-800 rounded-xl p-4 sm:p-6 shadow-xl">
            <div className="flex justify-between items-center mb-6">
  <h2 className="text-2xl font-semibold">Gestione Artisti</h2>
  <div className="flex gap-3">
    <button
      onClick={() => createDemoData(user.uid)}
      className="bg-green-600 hover:bg-green-700 px-4 py-2 rounded-lg transition-colors flex items-center gap-2"
    >
      <Plus className="w-5 h-5" />
      Crea Dati Demo
    </button>
    <button
      onClick={() => setShowArtistForm(true)}
      className="bg-purple-600 hover:bg-purple-700 px-4 py-2 rounded-lg transition-colors flex items-center gap-2"
    >
      <Plus className="w-5 h-5" />
      Nuovo Artista
    </button>
  </div>
</div>
<div className="space-y-4">
  <div className="flex justify-center gap-3">
  <button
    onClick={loadArtists}
    className="bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded-lg transition-colors"
  >
    🔄 Carica Artisti ({artists.length})
  </button>
  <button
    onClick={() => setArtists([])}
    className="bg-gray-600 hover:bg-gray-700 px-4 py-2 rounded-lg transition-colors"
  >
    👁️ Nascondi Lista
  </button>
  <button
    onClick={() => {
      openConfirmModal(
        `Sei sicuro di voler eliminare TUTTI i ${artists.length} artisti da Firebase? Questa azione è IRREVERSIBILE!`,
        async () => {
          try {
            for (const artist of artists) {
              await deleteDoc(doc(db, `users/${user.uid}/artists`, artist.id));
            }
            setArtists([]);
            openMessageBox('Tutti gli artisti sono stati eliminati definitivamente!', 'success');
          } catch (error) {
            openMessageBox('Errore nell\'eliminazione degli artisti.', 'error');
          }
        }
      );
    }}
    className="bg-red-600 hover:bg-red-700 px-4 py-2 rounded-lg transition-colors"
  >
    🗑️ Elimina Tutti
  </button>
</div>
  
  {artists.length === 0 ? (
    <div className="text-center py-8 text-gray-400">
      <p>Nessun artista caricato. Clicca "Carica Artisti" per vedere la lista.</p>
    </div>
  ) : (
    <div className="space-y-3">
      {artists.map((artist, index) => {
        const artistName = typeof artist === 'object' ? artist.name : artist;
        const artistData = typeof artist === 'object' ? artist : { name: artist };
        
        return (
          <div key={artist.id || index} className="bg-gray-700 rounded-lg p-4 flex justify-between items-center">
            <div>
              <h3 className="text-lg font-semibold text-white">{artistName}</h3>
              {artistData.specialties && <p className="text-purple-400">🎨 {artistData.specialties}</p>}
              {artistData.email && <p className="text-gray-300 text-sm">📧 {artistData.email}</p>}
              {artistData.phone && <p className="text-gray-300 text-sm">📞 {artistData.phone}</p>}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => {
                  setArtistFormData(artistData);
                  setEditingArtist(artistData);
                  setShowArtistForm(true);
                }}
                className="p-2 hover:bg-gray-600 rounded-lg transition-colors text-blue-400"
              >
                <Edit2 className="w-4 h-4" />
              </button>
              <button
                onClick={() => handleDeleteArtist(artist.id)}
                className="p-2 hover:bg-gray-600 rounded-lg transition-colors text-red-400"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        );
      })}
    </div>
  )}
</div>


          </div>
        )}

        {/* Appointment Form Modal */}
        {showAppointmentForm && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-800 rounded-xl p-6 shadow-2xl max-w-xl w-full relative border border-gray-700 max-h-[90vh] overflow-y-auto"> {/* Changed max-w-lg to max-w-xl */}
              <button
                onClick={resetForm}
                className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-700 transition-colors"
              >
                <X className="w-6 h-6 text-gray-400" />
              </button>
              <h2 className="text-2xl font-semibold mb-6 text-white">{editingAppointment ? 'Modifica Appuntamento' : 'Nuovo Appuntamento'}</h2>
              <form onSubmit={(e) => { e.preventDefault(); handleSubmitAppointment(); }} className="space-y-4">
                <div>
                  <label htmlFor="clientNameSelect" className="block text-sm font-medium text-gray-300 mb-1">Nome Cliente <span className="text-red-500">*</span></label>
                  <select
                    id="clientNameSelect"
                    value={formData.clientName}
                    onChange={handleClientSelect}
                    className="w-full px-3 py-2 bg-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                    required
                  >
                    <option value="">Seleziona un cliente</option>
                    {clients.map(client => (
                      <option key={client.id} value={client.name}>{client.name}</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => {
                      setEditingClient(null);
                      setShowClientForm(true);
                    }}
                    className="ml-2 mt-2 bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded-lg text-sm transition-colors"
                  >
                    Nuovo Cliente
                  </button>
                </div>

                <div>
                  <label htmlFor="clientPhone" className="block text-sm font-medium text-gray-300 mb-1">Telefono Cliente</label>
                  <input
                    type="tel"
                    id="clientPhone"
                    value={formData.clientPhone}
                    className="w-full px-3 py-2 bg-gray-700 rounded-lg text-gray-400"
                    readOnly
                  />
                  {/* WhatsApp link sotto al telefono */}
                  {formData.clientPhone && (
                    <a
                      href={`https://wa.me/${formData.clientPhone.replace(/\D/g, '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-2 inline-block text-green-400 underline text-sm"
                    >
                      Scrivi su WhatsApp
                    </a>
                  )}
                </div>
                <div>
                  <label htmlFor="clientEmail" className="block text-sm font-medium text-gray-300 mb-1">Email Cliente</label>
                  <input
                    type="email"
                    id="clientEmail"
                    value={formData.clientEmail}
                    className="w-full px-3 py-2 bg-gray-700 rounded-lg text-gray-400"
                    readOnly
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="date" className="block text-sm font-medium text-gray-300 mb-1">Data <span className="text-red-500">*</span></label>
                    <input
                      type="date"
                      id="date"
                      value={formData.date}
                      onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                      className="w-full px-3 py-2 bg-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
                      required
                    />
                  </div>
                  <div>
                    <label htmlFor="time" className="block text-sm font-medium text-gray-300 mb-1">Ora <span className="text-red-500">*</span></label>
                    <input
                      type="time"
                      id="time"
                      value={formData.time}
                      onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                      className="w-full px-3 py-2 bg-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
                      required
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="duration" className="block text-sm font-medium text-gray-300 mb-1">Durata (ore)</label>
                  <input
                    type="number"
                    id="duration"
                    value={formData.duration}
                    onChange={(e) => setFormData({ ...formData, duration: parseInt(e.target.value) || 0 })}
                    className="w-full px-3 py-2 bg-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
                    min="1"
                  />
                </div>
                <div>
                  <label htmlFor="project" className="block text-sm font-medium text-gray-300 mb-1">Progetto <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    id="project"
                    value={formData.project}
                    onChange={(e) => setFormData({ ...formData, project: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="artist" className="block text-sm font-medium text-gray-300 mb-1">Artista <span className="text-red-500">*</span></label>
                  <select
                    id="artist"
                    value={formData.artist}
                    onChange={(e) => setFormData({ ...formData, artist: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                    required
                  >
                    <option value="">Seleziona un artista</option>
                    {Array.isArray(artists) && artists.map((artist, index) => {
  const artistName = typeof artist === 'object' ? artist.name : artist;
  return (
    <option key={artist.id || index} value={artistName}>{artistName}</option>
  );
})}
                  </select>
                </div>
                <div>
                  <label htmlFor="notes" className="block text-sm font-medium text-gray-300 mb-1">Note</label>
                  <textarea
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 min-h-[80px]"
                  ></textarea>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="deposit" className="block text-sm font-medium text-gray-300 mb-1">Acconto (€)</label>
                    <input
                      type="number"
                      id="deposit"
                      value={formData.deposit}
                      onChange={(e) => setFormData({ ...formData, deposit: e.target.value })}
                      className="w-full px-3 py-2 bg-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
                      min="0"
                      step="any"
                    />
                  </div>
                  <div>
                    <label htmlFor="totalPrice" className="block text-sm font-medium text-gray-300 mb-1">Prezzo Totale (€)</label>
                    <input
                      type="number"
                      id="totalPrice"
                      value={formData.totalPrice}
                      onChange={(e) => setFormData({ ...formData, totalPrice: e.target.value })}
                      className="w-full px-3 py-2 bg-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
                      min="0"
                      step="any"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="status" className="block text-sm font-medium text-gray-300 mb-1">Stato</label>
                  <select
                    id="status"
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="confirmed">Confermato</option>
                    <option value="pending">In attesa</option>
                    <option value="cancelled">Cancellato</option>
                  </select>
                </div>

                {/* Photo and Sketch Uploads */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">Foto</label>
                    <input
                      type="file"
                      multiple
                      onChange={(e) => handlePhotoUpload(e, 'photos')}
                      className="hidden"
                      id="photo-upload"
                    />
                    <label htmlFor="photo-upload" className="flex items-center justify-center px-4 py-2 bg-gray-700 rounded-lg text-white cursor-pointer hover:bg-gray-600 transition-colors">
                      <Upload className="w-5 h-5 mr-2" /> Carica Foto
                    </label>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {formData.photos.map(photo => (
                        <div key={photo.id} className="relative">
                          <img 
  src={photo.url} 
  alt={photo.name} 
  className="w-20 h-20 object-cover rounded-md cursor-pointer hover:opacity-80 transition-opacity" 
  onClick={() => openImageModal(photo.url, photo.name)}
/>
                          <button
                            type="button"
                            onClick={() => removePhoto(photo.id, 'photos')}
                            className="absolute top-0 right-0 bg-red-600 rounded-full p-1 text-white text-xs hover:bg-red-700"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">Bozze</label>
                    <input
                      type="file"
                      multiple
                      onChange={(e) => handlePhotoUpload(e, 'sketches')}
                      className="hidden"
                      id="sketch-upload"
                    />
                    <label htmlFor="sketch-upload" className="flex items-center justify-center px-4 py-2 bg-gray-700 rounded-lg text-white cursor-pointer hover:bg-gray-600 transition-colors">
                      <Upload className="w-5 h-5 mr-2" /> Carica Bozze
                    </label>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {formData.sketches.map(sketch => (
                        <div key={sketch.id} className="relative">
                          <img 
  src={sketch.url} 
  alt={sketch.name} 
  className="w-20 h-20 object-cover rounded-md cursor-pointer hover:opacity-80 transition-opacity" 
  onClick={() => openImageModal(sketch.url, sketch.name)}
/>
                          <button
                            type="button"
                            onClick={() => removePhoto(sketch.id, 'sketches')}
                            className="absolute top-0 right-0 bg-red-600 rounded-full p-1 text-white text-xs hover:bg-red-700"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between gap-3 mt-6">
  {/* Bottone cancella visibile solo in modifica */}
  {editingAppointment && (
    <button
      type="button"
      onClick={handleDeleteAppointment}
      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
    >
      Cancella Appuntamento
    </button>
  )}

  <div className="flex justify-end gap-3 flex-1">
    <button
      type="button"
      onClick={resetForm}
      className="px-4 py-2 bg-gray-700 text-gray-200 rounded-lg hover:bg-gray-600 transition-colors"
    >
      Annulla
    </button>
    <button
      type="submit"
      className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
    >
      {editingAppointment ? 'Salva Modifiche' : 'Aggiungi Appuntamento'}
    </button>
  </div>
</div>
              </form>
            </div>
          </div>
        )}

        {/* Client Form Modal */}
{/* Artist Form Modal */}
        {showArtistForm && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-800 rounded-xl p-6 shadow-2xl max-w-xl w-full relative border border-gray-700 max-h-[90vh] overflow-y-auto">
              <button
                onClick={() => {
                  setShowArtistForm(false);
                  setEditingArtist(null);
                  setArtistFormData({ name: '', specialties: '', email: '', phone: '', notes: '' });
                }}
                className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-700 transition-colors"
              >
                <X className="w-6 h-6 text-gray-400" />
              </button>
              <h2 className="text-2xl font-semibold mb-6 text-white">
                {editingArtist ? 'Modifica Artista' : 'Nuovo Artista'}
              </h2>
              <form onSubmit={(e) => { e.preventDefault(); handleSubmitArtist(); }} className="space-y-4">
                <div>
                  <label htmlFor="artistName" className="block text-sm font-medium text-gray-300 mb-1">
                    Nome Artista <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="artistName"
                    value={artistFormData.name}
                    onChange={(e) => setArtistFormData({ ...artistFormData, name: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder="Nome dell'artista"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="specialties" className="block text-sm font-medium text-gray-300 mb-1">Specialità</label>
                  <input
                    type="text"
                    id="specialties"
                    value={artistFormData.specialties}
                    onChange={(e) => setArtistFormData({ ...artistFormData, specialties: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder="es. Realistico, Blackwork, Giapponese"
                  />
                </div>
                <div>
                  <label htmlFor="artistEmail" className="block text-sm font-medium text-gray-300 mb-1">Email</label>
                  <input
                    type="email"
                    id="artistEmail"
                    value={artistFormData.email}
                    onChange={(e) => setArtistFormData({ ...artistFormData, email: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder="email@esempio.com"
                  />
                </div>
                <div>
                  <label htmlFor="artistPhone" className="block text-sm font-medium text-gray-300 mb-1">Telefono</label>
                  <input
                    type="tel"
                    id="artistPhone"
                    value={artistFormData.phone}
                    onChange={(e) => setArtistFormData({ ...artistFormData, phone: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder="333-1234567"
                  />
                </div>
                <div>
                  <label htmlFor="artistNotes" className="block text-sm font-medium text-gray-300 mb-1">Note</label>
                  <textarea
                    id="artistNotes"
                    value={artistFormData.notes}
                    onChange={(e) => setArtistFormData({ ...artistFormData, notes: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 min-h-[80px]"
                    placeholder="Note aggiuntive sull'artista"
                  ></textarea>
                </div>

                <div className="flex justify-end gap-3 mt-6">
                  <button
                    type="button"
                    onClick={() => {
                      setShowArtistForm(false);
                      setEditingArtist(null);
                      setArtistFormData({ name: '', specialties: '', email: '', phone: '', notes: '' });
                    }}
                    className="px-4 py-2 bg-gray-700 text-gray-200 rounded-lg hover:bg-gray-600 transition-colors"
                  >
                    Annulla
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                  >
                    {editingArtist ? 'Salva Modifiche' : 'Aggiungi Artista'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
        {showClientForm && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-800 rounded-xl p-6 shadow-2xl max-w-xl w-full relative border border-gray-700 max-h-[90vh] overflow-y-auto"> {/* Changed max-w-lg to max-w-xl */}
              <button
                onClick={resetClientForm}
                className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-700 transition-colors"
              >
                <X className="w-6 h-6 text-gray-400" />
              </button>
              <h2 className="text-2xl font-semibold mb-6 text-white">{editingClient ? 'Modifica Cliente' : 'Nuovo Cliente'}</h2>
              <form onSubmit={(e) => { e.preventDefault(); handleSubmitClient(); }} className="space-y-4">
                <div>
                  <label htmlFor="clientName" className="block text-sm font-medium text-gray-300 mb-1">Nome <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    id="clientName"
                    value={clientFormData.name}
                    onChange={(e) => setClientFormData({ ...clientFormData, name: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="clientPhone" className="block text-sm font-medium text-gray-300 mb-1">Telefono <span className="text-red-500">*</span></label>
                  <input
                    type="tel"
                    id="clientPhone"
                    value={clientFormData.phone}
                    onChange={(e) => setClientFormData({ ...clientFormData, phone: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="clientEmail" className="block text-sm font-medium text-gray-300 mb-1">Email <span className="text-red-500">*</span></label>
                  <input
                    type="email"
                    id="clientEmail"
                    value={clientFormData.email}
                    onChange={(e) => setClientFormData({ ...clientFormData, email: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="clientNotes" className="block text-sm font-medium text-gray-300 mb-1">Note</label>
                  <textarea
                    id="clientNotes"
                    value={clientFormData.notes}
                    onChange={(e) => setClientFormData({ ...clientFormData, notes: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 min-h-[80px]"
                  ></textarea>
                </div>
                <div>
                  <label htmlFor="allergies" className="block text-sm font-medium text-gray-300 mb-1">Allergie</label>
                  <input
                    type="text"
                    id="allergies"
                    value={clientFormData.allergies}
                    onChange={(e) => setClientFormData({ ...clientFormData, allergies: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>
                <div>
                  <label htmlFor="preferredZones" className="block text-sm font-medium text-gray-300 mb-1">Zone Preferite</label>
                  <input
                    type="text"
                    id="preferredZones"
                    value={clientFormData.preferredZones}
                    onChange={(e) => setClientFormData({ ...clientFormData, preferredZones: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>
                <div>
                  <label htmlFor="preferredArtist" className="block text-sm font-medium text-gray-300 mb-1">Artista Preferito</label>
                  <select
                    id="preferredArtist"
                    value={clientFormData.preferredArtist}
                    onChange={(e) => setClientFormData({ ...clientFormData, preferredArtist: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="">Nessun artista preferito</option>
                    {Array.isArray(artists) && artists.map((artist, index) => {
  const artistName = typeof artist === 'object' ? artist.name : artist;
  return (
    <option key={artist.id || index} value={artistName}>{artistName}</option>
  );
})}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Foto di Riferimento</label>
                  <input
                    type="file"
                    multiple
                    onChange={(e) => handlePhotoUpload(e, 'referencePhotos', true)}
                    className="hidden"
                    id="client-photo-upload"
                  />
                  <label htmlFor="client-photo-upload" className="flex items-center justify-center px-4 py-2 bg-gray-700 rounded-lg text-white cursor-pointer hover:bg-gray-600 transition-colors">
                      <Upload className="w-5 h-5 mr-2" /> Carica Foto
                  </label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {clientFormData.referencePhotos.map(photo => (
                      <div key={photo.id} className="relative">
                        <img 
  src={photo.url} 
  alt={photo.name} 
  className="w-20 h-20 object-cover rounded-md cursor-pointer hover:opacity-80 transition-opacity" 
  onClick={() => openImageModal(photo.url, photo.name)}
/>
                        <button
                          type="button"
                          onClick={() => removePhoto(photo.id, 'referencePhotos', true)}
                          className="absolute top-0 right-0 bg-red-600 rounded-full p-1 text-white text-xs hover:bg-red-700"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="consent"
                    checked={clientFormData.consent}
                    onChange={(e) => setClientFormData({ ...clientFormData, consent: e.target.checked })}
                    className="form-checkbox h-5 w-5 text-purple-600 rounded border-gray-600 focus:ring-purple-500 bg-gray-700"
                  />
                  <label htmlFor="consent" className="ml-2 text-sm text-gray-300">Consenso informato firmato</label>
                </div>

                <div className="flex justify-end gap-3 mt-6">
                  <button
                    type="button"
                    onClick={resetClientForm}
                    className="px-4 py-2 bg-gray-700 text-gray-200 rounded-lg hover:bg-gray-600 transition-colors"
                  >
                    Annulla
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                  >
                    {editingClient ? 'Salva Modifiche' : 'Aggiungi Cliente'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>

      {/* Custom Modals rendered at the top level */}
      <ConfirmationModal
        message={confirmMessage}
        onConfirm={handleConfirm}
        onCancel={closeConfirmModal}
        show={showConfirmModal}
      />
      <MessageBox
        message={messageBoxMessage}
        type={messageBoxType}
        onClose={closeMessageBox}
        show={showMessageBox}
      />



{/* AGGIUNGI QUI IL NUOVO MODAL: */}
{/* Modal Alert Reminder */}
{showReminderAlert && (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div className="bg-gray-800 p-6 rounded-lg w-96 max-w-full mx-4 border border-yellow-500">
      <div className="flex items-center mb-4">
        <span className="text-2xl mr-3">⚠️</span>
        <h3 className="text-xl font-bold text-yellow-400">Reminder da inviare!</h3>
      </div>
      
      <p className="text-gray-300 mb-4">
  Hai <strong>{pendingReminders.length}</strong> appuntament{pendingReminders.length > 1 ? 'i' : 'o'} nei prossimi 2 giorni senza reminder inviat{pendingReminders.length > 1 ? 'i' : 'o'}:
</p>
      
      <div className="bg-gray-700 p-3 rounded-lg mb-4 max-h-40 overflow-y-auto">
        {pendingReminders.map(apt => (
          <div key={apt.id} className="text-sm text-gray-300 mb-2">
            📅 {new Date(apt.date).toLocaleDateString('it-IT')} - 
            <strong> {apt.clientName}</strong> ({apt.project})
          </div>
        ))}
      </div>
      
      <div className="flex space-x-2">
  <button
    onClick={() => {
      setShowReminderAlert(false);
      
      // Cancella timeout precedente se esiste
      if (reminderTimeout) {
        clearTimeout(reminderTimeout);
      }
      
      // Imposta nuovo reminder per 2 ore
      const newTimeout = setTimeout(() => {
        checkPendingReminders();
      }, 2 * 60 * 60 * 1000); // 2 ore in millisecondi
      
      setReminderTimeout(newTimeout);
    }}
    className="flex-1 px-3 py-2 bg-gray-600 hover:bg-gray-500 rounded-lg text-white transition-colors text-sm"
  >
    Ricorda tra 2h
  </button>
  <button
  onClick={() => {
    // Aggiungi tutti i reminder pendenti alla lista dismissed
    const newDismissed = new Set(dismissedReminders);
    pendingReminders.forEach(apt => newDismissed.add(apt.id));
    setDismissedReminders(newDismissed);
    
    // Salva nel localStorage per persistenza
    localStorage.setItem('dismissedReminders', JSON.stringify([...newDismissed]));
    
    setShowReminderAlert(false);
  }}
  className="flex-1 px-3 py-2 bg-red-600 hover:bg-red-700 rounded-lg text-white transition-colors text-sm"
>
  Non inviare
</button>
  <button
    onClick={() => {
      setShowReminderAlert(false);
      sendBulkReminders();
    }}
    className="flex-1 px-3 py-2 bg-yellow-600 hover:bg-yellow-700 rounded-lg text-white transition-colors text-sm"
  >
    Invia ora
  </button>
</div>
    </div>
  </div>
)}

{/* Modal Visualizzazione Immagine */}
{showImageModal && selectedImage && (
  <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
    <div className="bg-gray-800 rounded-lg max-w-4xl max-h-full overflow-auto">
      <div className="flex justify-between items-center p-4 border-b border-gray-700">
        <h3 className="text-lg font-bold text-white">{selectedImage.name}</h3>
        <button
          onClick={() => setShowImageModal(false)}
          className="text-gray-400 hover:text-white text-2xl"
        >
          ✕
        </button>
      </div>
      <div className="p-4">
        <img
          src={selectedImage.url}
          alt={selectedImage.name}
          className="max-w-full max-h-96 object-contain mx-auto rounded-lg"
        />
      </div>
    </div>
  </div>
)}

</div>
);
};
export default TattooScheduler;
